import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { Users, BookHeart, Plus, Trash2, ShieldCheck, Calendar, MessagesSquare, Clock, Pencil, X, KeyRound, ClipboardCheck, Check, XCircle, FileDown, Mail, Phone, MapPin, History, ArrowRight, Filter, MessageCircle, User as UserIcon, ExternalLink, FileSearch, Send, BarChart3, TrendingUp, LogIn, Megaphone, CalendarCheck, AlertTriangle, CalendarClock, Bell, HeartHandshake, LayoutDashboard, Menu, Globe2, HeartPulse, Gauge, Timer, FileCheck2, ShieldAlert, Coins, Filter as FilterIcon } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "sonner";
import AdminOverview from "./admin/AdminOverview";
import { ImpactReport, WellnessOutcomesReport, UtilizationReport, FunnelReport, ResponseTimesReport, NotesComplianceReport, RiskRegisterReport, DonationsReport } from "./admin/AdvancedReports";

// ---------- Left-sidebar navigation, grouped for clarity ----------
// Each group renders as a labelled section in the sidebar. Every leaf is a `tab` id
// that maps to the existing conditional-render blocks below, so we get a cleaner IA
// without rewiring every section.
const NAV_GROUPS = [
  {
    label: "",
    items: [{ id: "overview", label: "Overview", icon: LayoutDashboard }],
  },
  {
    label: "People",
    items: [
      { id: "users", label: "Users", icon: Users },
      { id: "applications", label: "Applications", icon: ClipboardCheck },
      { id: "counsellors", label: "Add counsellor", icon: ShieldCheck },
    ],
  },
  {
    label: "Operations",
    items: [
      { id: "bookings", label: "Bookings", icon: Calendar },
      { id: "chats", label: "Chat activity", icon: MessagesSquare },
      { id: "communication", label: "Communication", icon: Megaphone },
      { id: "emails", label: "Email status", icon: Send },
    ],
  },
  {
    label: "Insights",
    items: [{ id: "reports", label: "Reports", icon: BarChart3 }],
  },
  {
    label: "System",
    items: [
      { id: "audit", label: "Audit log", icon: History },
      { id: "resources", label: "Self-help videos", icon: BookHeart },
    ],
  },
];

// Reports have their own sub-navigation to prevent the vertical scroll monster.
const REPORT_GROUPS = [
  {
    label: "Sessions",
    items: [
      { id: "upcoming", label: "Upcoming bookings", icon: CalendarClock },
      { id: "new", label: "New bookings", icon: CalendarCheck },
      { id: "roster", label: "Roster (next 7 days)", icon: Calendar },
      { id: "past", label: "Past bookings", icon: History },
    ],
  },
  {
    label: "People & attendance",
    items: [
      { id: "attendance", label: "Attendance & outcomes", icon: ClipboardCheck },
      { id: "utilization", label: "Counsellor utilization", icon: Gauge },
      { id: "notes", label: "Notes compliance", icon: FileCheck2 },
      { id: "contribution", label: "Contribution & appreciation", icon: HeartHandshake },
    ],
  },
  {
    label: "Impact & funding",
    items: [
      { id: "impact", label: "Impact dashboard", icon: Globe2 },
      { id: "wellness", label: "Wellness outcomes", icon: HeartPulse },
      { id: "funnel", label: "Client funnel", icon: FilterIcon },
      { id: "sla", label: "Response times / SLAs", icon: Timer },
      { id: "risk", label: "Risk & escalation", icon: ShieldAlert },
      { id: "donations", label: "Donations ledger", icon: Coins },
    ],
  },
];

const STATUS_PILL = {
  scheduled: "bg-secondary/15 text-secondary border-secondary/30",
  completed: "bg-accent/30 text-[#3D405B] border-accent/50",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
  no_show: "bg-muted text-[#5F627D] border-[#3D405B]/10",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminPanel() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [chatActivity, setChatActivity] = useState([]);
  const [resources, setResources] = useState([]);
  const [applications, setApplications] = useState([]);
  const [appFilter, setAppFilter] = useState("pending");
  const [reviewingApp, setReviewingApp] = useState(null);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userProvinceFilter, setUserProvinceFilter] = useState("all");
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", bio: "", specialties: "", languages: "", office_address: "", office_map_url: "" });
  const [resForm, setResForm] = useState({ title: "", description: "", youtube_url: "", category: "Coping skills" });

  const filteredBookings = useMemo(
    () => bookings.filter((b) => bookingFilter === "all" || b.status === bookingFilter),
    [bookings, bookingFilter],
  );

  // Group by counsellor. Within each group: upcoming (soonest first) then past (most recent first).
  const bookingsByCounsellor = useMemo(() => {
    const nowIso = new Date().toISOString();
    const groups = {};
    for (const b of filteredBookings) {
      const key = b.counsellor?.user_id || "__unassigned__";
      const label = b.counsellor?.name || "Unassigned / deleted counsellor";
      if (!groups[key]) groups[key] = { label, upcoming: [], past: [] };
      const bucket = (b.status === "scheduled" && (b.scheduled_at || "") >= nowIso) ? "upcoming" : "past";
      groups[key][bucket].push(b);
    }
    Object.values(groups).forEach((g) => {
      g.upcoming.sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
      g.past.sort((a, b) => (b.scheduled_at || "").localeCompare(a.scheduled_at || ""));
    });
    // Return array sorted by counsellor name (unassigned last)
    return Object.entries(groups)
      .sort(([ka, ga], [kb, gb]) => {
        if (ka === "__unassigned__") return 1;
        if (kb === "__unassigned__") return -1;
        return (ga.label || "").localeCompare(gb.label || "");
      })
      .map(([user_id, g]) => ({ user_id, ...g }));
  }, [filteredBookings]);
  const filteredApplications = useMemo(
    () => applications.filter((a) => appFilter === "all" || a.status === appFilter),
    [applications, appFilter],
  );

  const provinceOptions = useMemo(() => {
    const set = new Set();
    users.forEach((u) => { if (u.province) set.add(u.province); });
    return Array.from(set).sort();
  }, [users]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (userRoleFilter !== "all" && u.role !== userRoleFilter) return false;
      if (userProvinceFilter !== "all") {
        if (userProvinceFilter === "__unknown__" && u.province) return false;
        if (userProvinceFilter !== "__unknown__" && u.province !== userProvinceFilter) return false;
      }
      return true;
    });
  }, [users, userRoleFilter, userProvinceFilter]);

  const roleCounts = useMemo(() => {
    const c = { all: users.length, client: 0, counsellor: 0, parent: 0, admin: 0 };
    users.forEach((u) => { if (c[u.role] != null) c[u.role] += 1; });
    return c;
  }, [users]);

  const load = async () => {
    try {
      const [u, b, c, r, a] = await Promise.all([
        api.get("/admin/users"),
        api.get("/bookings"),
        api.get("/admin/chat-activity"),
        api.get("/resources"),
        api.get("/admin/applications"),
      ]);
      setUsers(u.data);
      setBookings(b.data);
      setChatActivity(c.data);
      setResources(r.data);
      setApplications(a.data);
    } catch (e) {
      logger.warn("admin load failed:", e?.message);
      toast.error("Could not load admin data");
    }
  };
  useEffect(() => { load(); }, []);

  const addCounsellor = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: form.email,
        password: form.password,
        name: form.name,
        bio: form.bio,
        specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        office_address: form.office_address.trim() || undefined,
        office_map_url: form.office_map_url.trim() || undefined,
      };
      await api.post("/admin/counsellors", payload);
      toast.success("Counsellor added");
      setForm({ email: "", password: "", name: "", bio: "", specialties: "", languages: "", office_address: "", office_map_url: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };

  const addResource = async (e) => {
    e.preventDefault();
    try {
      await api.post("/resources", resForm);
      toast.success("Resource added");
      setResForm({ title: "", description: "", youtube_url: "", category: "Coping skills" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };

  const delUser = async (uid) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const resendWelcome = async (uid, name) => {
    if (!window.confirm(`Resend the welcome / set-password email to ${name}? Any older reset link will stop working.`)) return;
    try {
      await api.post(`/admin/counsellors/${uid}/resend-welcome`);
      toast.success(`Welcome email resent to ${name}. Link is valid for 7 days.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend welcome");
    }
  };

  const delResource = async (id) => {
    if (!window.confirm("Remove this video?")) return;
    try { await api.delete(`/resources/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 flex gap-6">
        {/* Left sidebar — grouped nav, collapsible on mobile */}
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed left-3 top-20 z-30 bg-white border border-[#3D405B]/15 rounded-full p-2 shadow-md"
          data-testid="admin-sidebar-toggle"
          aria-label="Open admin menu"
        >
          <Menu size={16} />
        </button>
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 bg-[#3D405B]/40 z-40" onClick={() => setSidebarOpen(false)} />
        )}
        <aside
          className={`fixed lg:sticky top-0 lg:top-6 h-screen lg:h-[calc(100vh-4rem)] w-64 bg-white border-r lg:border border-[#3D405B]/10 lg:rounded-3xl overflow-y-auto z-50 transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
          data-testid="admin-sidebar"
        >
          <div className="p-5 border-b border-[#3D405B]/10">
            <div className="overline">Admin</div>
            <div className="font-extrabold text-[#3D405B] text-lg leading-tight mt-1">Hello Foundation</div>
          </div>
          <nav className="p-3 space-y-4">
            {NAV_GROUPS.map((group, gi) => (
              <div key={gi}>
                {group.label && <div className="px-3 pb-1 text-[10px] uppercase font-extrabold tracking-[0.18em] text-[#7A7D96]">{group.label}</div>}
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setParams({ tab: item.id }); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-bold transition-all ${
                      tab === item.id
                        ? "bg-primary text-white shadow-sm"
                        : "text-[#3D405B] hover:bg-[#3D405B]/5"
                    }`}
                    data-testid={`admin-tab-${item.id}`}
                  >
                    <item.icon size={15} /> {item.label}
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          {tab === "overview" && <AdminOverview onNavigate={(target) => {
            // Support "tab" or "tab:sub" navigation from Overview attention cards.
            const [t, sub] = String(target || "").split(":");
            if (sub) setParams({ tab: t, sub });
            else setParams({ tab: t });
          }} />}

        {tab === "users" && (
          <div className="space-y-4" data-testid="admin-users-list">
            <div className="brand-card p-4 flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-wider text-[#7A7D96]">Role</span>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "all", label: "All" },
                  { id: "client", label: "Clients" },
                  { id: "counsellor", label: "Counsellors" },
                  { id: "parent", label: "Parents" },
                  { id: "admin", label: "Admins" },
                ].map((chip) => (
                  <button
                    key={chip.id}
                    onClick={() => setUserRoleFilter(chip.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${userRoleFilter === chip.id ? "bg-primary text-white" : "bg-white border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"}`}
                    data-testid={`user-role-chip-${chip.id}`}
                  >
                    {chip.label}
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${userRoleFilter === chip.id ? "bg-white/25" : "bg-[#3D405B]/5"}`}>
                      {roleCounts[chip.id]}
                    </span>
                  </button>
                ))}
              </div>

              <div className="h-6 w-px bg-[#3D405B]/10 hidden sm:block" />

              <div className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white border border-[#3D405B]/15">
                <MapPin size={14} className="text-secondary" />
                <select
                  value={userProvinceFilter}
                  onChange={(e) => setUserProvinceFilter(e.target.value)}
                  className="bg-transparent text-sm font-bold text-[#3D405B] outline-none py-1.5 pr-2"
                  data-testid="user-province-filter"
                >
                  <option value="all">All provinces</option>
                  {provinceOptions.map((p) => (<option key={p} value={p}>{p}</option>))}
                  <option value="__unknown__">Unknown / not set</option>
                </select>
              </div>

              <span className="ml-auto text-sm font-semibold text-[#7A7D96]" data-testid="user-filter-count">
                {filteredUsers.length} of {users.length}
              </span>
            </div>

            {filteredUsers.length === 0 ? (
              <div className="brand-card p-10 text-center text-[#7A7D96]">No users match the current filters.</div>
            ) : filteredUsers.map((u) => (
              <div key={u.user_id} className="brand-card p-5" data-testid={`admin-user-card-${u.user_id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-12 h-12 rounded-2xl object-cover shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center font-extrabold text-[#3D405B] shrink-0">
                        {(u.name || "?")[0]}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span className="font-extrabold text-[#3D405B]">{u.name}</span>
                        <span className="pill bg-secondary/15 text-secondary capitalize">{u.role}</span>
                        {u.active === false && <span className="pill bg-destructive/10 text-destructive">inactive</span>}
                        {u.verification_status === "pending_verification" && <span className="pill bg-accent/25 text-[#3D405B]">pending review</span>}
                        {u.verification_status === "rejected" && <span className="pill bg-destructive/10 text-destructive">rejected</span>}
                        {u.role === "counsellor" && !u.last_login_at && u.verification_status !== "pending_verification" && (
                          <span className="pill bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-1" data-testid={`admin-user-neverloggedin-${u.user_id}`} title="Counsellor has not yet activated their account">
                            <Clock size={10} /> never logged in
                          </span>
                        )}
                        {u.whatsapp_opt_in && <span className="pill bg-primary/10 text-primary inline-flex items-center gap-1"><MessageCircle size={10} /> WhatsApp</span>}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-[#5F627D]">
                        <a href={`mailto:${u.email}`} className="inline-flex items-center gap-1.5 hover:text-primary truncate"><Mail size={11} /> {u.email}</a>
                        {u.phone && <a href={`tel:${u.phone}`} className="inline-flex items-center gap-1.5 hover:text-primary"><Phone size={11} /> {u.phone}</a>}
                        {u.age != null && <span className="inline-flex items-center gap-1.5">🎂 age {u.age}</span>}
                        {u.city && <span className="inline-flex items-center gap-1.5"><MapPin size={11} /> {[u.suburb, u.city, u.province].filter(Boolean).join(", ")}</span>}
                        {u.professional_body && <span className="inline-flex items-center gap-1.5"><ShieldCheck size={11} /> {u.professional_body} · {u.registration_number || "?"}</span>}
                        {u.years_experience != null && <span className="inline-flex items-center gap-1.5">👩‍⚕️ {u.years_experience}y experience</span>}
                        {u.office_address && <span className="inline-flex items-center gap-1.5 sm:col-span-2"><MapPin size={11} className="text-secondary" /> {u.office_address}</span>}
                        {u.languages?.length > 0 && <span className="inline-flex items-center gap-1.5 sm:col-span-2">🗣️ {u.languages.join(", ")}</span>}
                        {u.specialties?.length > 0 && <span className="inline-flex items-center gap-1.5 sm:col-span-2">💛 {u.specialties.join(", ")}</span>}
                      </div>
                      {u.bio && <p className="text-xs text-[#5F627D] mt-2 line-clamp-2">{u.bio}</p>}
                      <div className="text-[10px] text-[#7A7D96] mt-1">
                        {u.user_id} · joined {fmtDateTime(u.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex md:flex-col gap-2 md:min-w-[140px]">
                    <RouterLink to={`/admin/users/${u.user_id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-primary text-white text-xs font-bold hover:-translate-y-0.5 transition-all" data-testid={`admin-view-user-${u.user_id}`}>
                      <FileSearch size={13} /> View
                    </RouterLink>
                    <button onClick={() => setEditingUser(u)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#3D405B]/15 text-[#3D405B] text-xs font-bold hover:bg-[#3D405B]/5" data-testid={`admin-edit-user-${u.user_id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    {u.role === "counsellor" && !u.last_login_at && u.verification_status !== "pending_verification" && (
                      <button
                        onClick={() => resendWelcome(u.user_id, u.name)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold hover:bg-amber-100"
                        data-testid={`admin-resend-welcome-${u.user_id}`}
                        title="Resend the welcome / set-password email"
                      >
                        <Send size={12} /> Resend welcome
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <button onClick={() => delUser(u.user_id)} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-destructive border border-destructive/20 text-xs font-bold hover:bg-destructive/5" data-testid={`admin-delete-user-${u.user_id}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div data-testid="admin-bookings-list">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {["all", "scheduled", "completed", "cancelled"].map((s) => (
                <button key={s} onClick={() => setBookingFilter(s)} className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${bookingFilter === s ? "bg-secondary text-white shadow-sm" : "bg-white border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"}`} data-testid={`admin-booking-filter-${s}`}>
                  {s}
                </button>
              ))}
              <span className="ml-auto text-sm text-[#7A7D96]">{filteredBookings.length} session{bookings.length === 1 ? "" : "s"}</span>
            </div>
            <div className="space-y-5">
              {filteredBookings.length === 0 ? (
                <div className="brand-card px-5 py-10 text-center text-sm text-[#7A7D96]">No bookings match this filter.</div>
              ) : bookingsByCounsellor.map((group) => {
                const total = group.upcoming.length + group.past.length;
                return (
                  <details key={group.user_id} open className="brand-card overflow-hidden" data-testid={`admin-bookings-group-${group.user_id}`}>
                    <summary className="cursor-pointer px-5 py-4 flex flex-wrap items-center gap-3 hover:bg-[#3D405B]/[.02] transition-colors list-none">
                      <div className="w-10 h-10 rounded-2xl bg-accent flex items-center justify-center font-extrabold text-[#3D405B]">{group.label?.[0] || "?"}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-extrabold text-[#3D405B] truncate">{group.label}</div>
                        <div className="text-xs text-[#7A7D96]">{group.upcoming.length} upcoming · {group.past.length} past</div>
                      </div>
                      <span className="pill bg-primary/10 text-primary">{total} session{total === 1 ? "" : "s"}</span>
                    </summary>
                    <div>
                      {[...group.upcoming, ...group.past].map((b) => (
                        <div key={b.booking_id} className="border-t border-[#3D405B]/5 p-5 hover:bg-[#3D405B]/[.02] transition-colors" data-testid={`admin-booking-row-${b.booking_id}`}>
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                              <div>
                                <div className="overline mb-1">When</div>
                                <div className="flex items-center gap-1.5 text-[#3D405B] font-bold"><Clock size={12} className="text-primary" /> {fmtDateTime(b.scheduled_at)}</div>
                                <div className="text-[10px] text-[#7A7D96] mt-0.5">{b.duration_minutes || 50} min</div>
                              </div>
                              <div className="min-w-0">
                                <div className="overline mb-1">Client</div>
                                {b.client ? (
                                  <>
                                    <div className="font-bold text-[#3D405B] truncate">{b.client.name}</div>
                                    {b.client.email && <a href={`mailto:${b.client.email}`} className="text-[11px] text-[#5F627D] hover:text-primary flex items-center gap-1 truncate" data-testid={`admin-booking-client-email-${b.booking_id}`}><Mail size={10} className="shrink-0" /> <span className="truncate">{b.client.email}</span></a>}
                                    {b.client.phone && <a href={`tel:${b.client.phone}`} className="text-[11px] text-[#5F627D] hover:text-primary flex items-center gap-1" data-testid={`admin-booking-client-phone-${b.booking_id}`}><Phone size={10} className="shrink-0" /> {b.client.phone}</a>}
                                  </>
                                ) : <span className="text-[#7A7D96] italic text-xs">deleted user</span>}
                              </div>
                              <div>
                                <div className="overline mb-1">Mode · Status</div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="pill bg-primary/10 text-primary capitalize">{(b.session_mode || "online").replace("_", " ")}</span>
                                  {b.session_type && <span className="pill bg-muted text-[#5F627D] capitalize">{b.session_type.replace("_", " ")}</span>}
                                  <span className={`pill border ${STATUS_PILL[b.status] || STATUS_PILL.scheduled}`}>{b.status}</span>
                                </div>
                                {b.reminder_sent_at && <div className="text-[10px] text-[#7A7D96] mt-1">Reminder sent ✓</div>}
                              </div>
                            </div>
                            <div className="flex md:flex-col gap-2">
                              <RouterLink to={`/session/${b.booking_id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#3D405B]/15 text-[#3D405B] text-xs font-bold hover:bg-[#3D405B]/5" data-testid={`admin-booking-open-${b.booking_id}`}>
                                <ExternalLink size={12} /> Open
                              </RouterLink>
                            </div>
                          </div>
                          {(b.topic || b.notes_private || b.office_address || b.meeting_url) && (
                            <div className="mt-3 pt-3 border-t border-[#3D405B]/5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#5F627D]">
                              {b.topic && <div><strong className="text-[#3D405B]">Topic:</strong> {b.topic}</div>}
                              {b.session_mode === "online" && b.meeting_url && <div className="truncate"><strong className="text-[#3D405B]">Meeting:</strong> <a href={b.meeting_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{b.meeting_url}</a></div>}
                              {b.session_mode === "in_person" && b.office_address && <div><strong className="text-[#3D405B]">Office:</strong> {b.office_address}</div>}
                              {b.notes_private && <div className="sm:col-span-2 line-clamp-2"><strong className="text-[#3D405B]">Notes:</strong> {b.notes_private}</div>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        )}

        {tab === "chats" && (
          <div data-testid="admin-chats-list">
            <div className="brand-card p-5 mb-5 bg-accent/15 text-[#3D405B] text-sm">
              <strong>Privacy by design.</strong> You see who&apos;s chatting and how often — but not what&apos;s said. That stays between client and counsellor.
            </div>
            {chatActivity.length === 0 ? (
              <div className="brand-card p-10 text-center text-[#7A7D96]">No conversations yet.</div>
            ) : (
              <div className="brand-card overflow-hidden">
                <div className="grid grid-cols-12 px-5 py-3 bg-muted text-xs font-bold uppercase tracking-wider text-[#7A7D96]">
                  <div className="col-span-6">Participants</div>
                  <div className="col-span-3">Messages</div>
                  <div className="col-span-3 text-right">Last activity</div>
                </div>
                {chatActivity.map((t) => (
                  <div key={t.thread_id} className="grid grid-cols-12 px-5 py-4 border-t border-[#3D405B]/5 items-center text-sm" data-testid={`admin-chat-row-${t.thread_id}`}>
                    <div className="col-span-6">
                      <div className="flex flex-wrap items-center gap-2">
                        {t.participants.map((p) => (
                          <span key={p.user_id} className="inline-flex items-center gap-1.5">
                            {p.picture ? <img src={p.picture} className="w-6 h-6 rounded-full object-cover" alt="" /> : <span className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-[10px] font-bold text-[#3D405B]">{p.name?.[0]}</span>}
                            <span className="font-bold text-[#3D405B]">{p.name}</span>
                            <span className="text-[10px] uppercase tracking-wider text-[#7A7D96]">{p.role}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-3 font-bold text-primary">{t.message_count}</div>
                    <div className="col-span-3 text-right text-[#5F627D]">{fmtDateTime(t.last_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "applications" && (
          <div data-testid="admin-applications-list">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {["pending", "approved", "rejected", "all"].map((s) => (
                <button key={s} onClick={() => setAppFilter(s)} className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${appFilter === s ? "bg-secondary text-white shadow-sm" : "bg-white border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"}`} data-testid={`admin-app-filter-${s}`}>
                  {s}
                </button>
              ))}
              <span className="ml-auto text-sm text-[#7A7D96]">
                {filteredApplications.length} application(s)
              </span>
            </div>
            {filteredApplications.length === 0 ? (
              <div className="brand-card p-10 text-center text-[#7A7D96]">No {appFilter === "all" ? "" : appFilter} applications yet.</div>
            ) : (
              <div className="space-y-4">
                {filteredApplications.map((a) => (
                  <ApplicationCard key={a.application_id} app={a} onReview={() => setReviewingApp(a)} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "audit" && <AuditLogTab />}

        {tab === "emails" && <EmailLogTab />}

        {tab === "reports" && <ReportsTab />}

        {tab === "communication" && <CommunicationTab />}

        {tab === "counsellors" && (
          <form onSubmit={addCounsellor} className="brand-card p-7 max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="admin-add-counsellor-form">
            <h3 className="sm:col-span-2 text-xl font-extrabold text-[#3D405B]">Add a counsellor</h3>
            <div><label className="text-xs font-bold uppercase tracking-wider text-[#3D405B]">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 mt-1 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wider text-[#3D405B]">Email</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 mt-1 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold uppercase tracking-wider text-[#3D405B]">Password</label><input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-4 py-3 mt-1 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold uppercase tracking-wider text-[#3D405B]">Bio</label><textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full px-4 py-3 mt-1 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none resize-none" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wider text-[#3D405B]">Specialties (comma-sep)</label><input value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} placeholder="Anxiety, Bullying" className="w-full px-4 py-3 mt-1 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" /></div>
            <div><label className="text-xs font-bold uppercase tracking-wider text-[#3D405B]">Languages</label><input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} placeholder="English, isiZulu" className="w-full px-4 py-3 mt-1 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold uppercase tracking-wider text-[#3D405B]">Office address (for in-person sessions)</label><input value={form.office_address} onChange={(e) => setForm({ ...form, office_address: e.target.value })} placeholder="123 Main Rd, Rosebank, Johannesburg" className="w-full px-4 py-3 mt-1 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" data-testid="admin-counsellor-address-input" /><div className="text-xs text-[#7A7D96] mt-1">Leave blank if this counsellor only sees clients online.</div></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold uppercase tracking-wider text-[#3D405B]">Google Maps link (optional)</label><input value={form.office_map_url} onChange={(e) => setForm({ ...form, office_map_url: e.target.value })} placeholder="https://maps.app.goo.gl/…" className="w-full px-4 py-3 mt-1 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" /></div>
            <div className="sm:col-span-2"><button type="submit" className="px-6 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all inline-flex items-center gap-2" data-testid="admin-create-counsellor-btn"><Plus size={15} /> Create counsellor</button></div>
          </form>
        )}

        {tab === "resources" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={addResource} className="brand-card p-6 space-y-3 lg:col-span-1" data-testid="admin-add-resource-form">
              <h3 className="text-lg font-extrabold text-[#3D405B]">Add a video</h3>
              <input required placeholder="YouTube URL" value={resForm.youtube_url} onChange={(e) => setResForm({ ...resForm, youtube_url: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" />
              <input required placeholder="Title" value={resForm.title} onChange={(e) => setResForm({ ...resForm, title: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" />
              <input placeholder="Category" value={resForm.category} onChange={(e) => setResForm({ ...resForm, category: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none" />
              <textarea rows={3} placeholder="Description" value={resForm.description} onChange={(e) => setResForm({ ...resForm, description: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 outline-none resize-none" />
              <button type="submit" className="px-6 py-2.5 rounded-full bg-primary text-white font-bold shadow-md inline-flex items-center gap-2"><Plus size={15} /> Add</button>
            </form>
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {resources.map((r) => (
                <div key={r.resource_id} className="brand-card overflow-hidden">
                  <div className="aspect-video relative bg-muted">
                    {r.thumbnail && <img src={r.thumbnail} alt="" className="w-full h-full object-cover" />}
                    <span className="absolute top-2 left-2 pill bg-card/95 text-[#3D405B]">{r.category}</span>
                  </div>
                  <div className="p-4">
                    <div className="font-bold text-[#3D405B] text-sm line-clamp-2">{r.title}</div>
                    <button onClick={() => delResource(r.resource_id)} className="mt-2 text-xs text-destructive inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </main>
      </div>
      {editingUser && (
        <EditUserModal
          initial={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); load(); }}
        />
      )}
      {reviewingApp && (
        <ReviewApplicationModal
          app={reviewingApp}
          onClose={() => setReviewingApp(null)}
          onReviewed={() => { setReviewingApp(null); load(); }}
        />
      )}
    </div>
  );
}

function EditUserModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial.name || "");
  const [email, setEmail] = useState(initial.email || "");
  const [role, setRole] = useState(initial.role || "client");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    const changes = {};
    if (name.trim() !== (initial.name || "")) changes.name = name.trim();
    if (email.trim().toLowerCase() !== (initial.email || "").toLowerCase()) changes.email = email.trim().toLowerCase();
    if (role !== initial.role) changes.role = role;
    if (password) {
      if (password.length < 10) { toast.error("Password must be at least 10 characters"); return; }
      changes.password = password;
    }
    if (Object.keys(changes).length === 0) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${initial.user_id}`, changes);
      const rev = data._sessions_revoked || 0;
      if (changes.password && rev > 0) {
        toast.success(`Saved · signed the user out of ${rev} session${rev === 1 ? "" : "s"}`);
      } else {
        toast.success("Saved");
      }
      onSaved?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save";
      toast.error(typeof msg === "string" ? msg : "Could not save");
      logger.warn("admin user update failed:", err?.message);
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none text-sm";
  const labelCls = "text-xs uppercase font-extrabold tracking-[0.18em] text-[#7A7D96] mb-1.5 block";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#3D405B]/40 backdrop-blur-sm" data-testid="admin-edit-user-modal" onClick={onClose}>
      <div className="brand-card p-7 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Edit user</div>
            <h2 className="text-2xl font-extrabold text-[#3D405B]">{initial.name}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#3D405B]/5" aria-label="Close" data-testid="admin-edit-close-btn"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} required data-testid="admin-edit-name" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required data-testid="admin-edit-email" />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} data-testid="admin-edit-role">
              <option value="client">Client (youth)</option>
              <option value="parent">Parent / caregiver</option>
              <option value="counsellor">Counsellor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="pt-2 border-t border-[#3D405B]/5">
            <label className={labelCls}>
              <KeyRound size={11} className="inline mr-1 mb-0.5" /> Reset password
              <span className="normal-case font-bold text-[#7A7D96] tracking-normal"> — leave blank to keep current</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls + " pr-24"}
                placeholder="Min 10 characters"
                autoComplete="new-password"
                data-testid="admin-edit-password"
              />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#7A7D96] hover:text-[#3D405B] px-2" data-testid="admin-edit-toggle-password">
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {password && <p className="text-xs text-[#7A7D96] mt-1.5">Saving will sign the user out of all sessions.</p>}
          </div>

          <div className="flex gap-2 justify-end pt-3 border-t border-[#3D405B]/5">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-[#3D405B]/15 font-bold text-[#3D405B] hover:bg-[#3D405B]/5">Cancel</button>
            <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50" data-testid="admin-edit-save-btn">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const APP_STATUS_PILL = {
  pending: "bg-accent/25 text-[#3D405B] border-accent/50",
  approved: "bg-secondary/15 text-secondary border-secondary/30",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

function ApplicationCard({ app, onReview }) {
  return (
    <div className="brand-card p-6" data-testid={`admin-app-card-${app.application_id}`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`pill border ${APP_STATUS_PILL[app.status] || APP_STATUS_PILL.pending}`}>{app.status}</span>
            <span className="pill bg-primary/10 text-primary">{app.professional_body}</span>
            <span className="pill bg-muted text-[#5F627D]">{app.registration_number}</span>
            {app.years_experience != null && <span className="pill bg-muted text-[#5F627D]">{app.years_experience}y exp</span>}
          </div>
          <h3 className="text-xl font-extrabold text-[#3D405B]">{app.name}</h3>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#5F627D]">
            <span className="inline-flex items-center gap-1.5"><Mail size={13} /> {app.email}</span>
            {app.phone && <span className="inline-flex items-center gap-1.5"><Phone size={13} /> {app.phone}</span>}
            {app.city && <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {app.city}</span>}
          </div>
          {app.bio && <p className="text-sm text-[#3D405B] mt-3 line-clamp-2">{app.bio}</p>}
          {(app.specialties?.length > 0 || app.languages?.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {app.specialties?.map((s) => <span key={`s-${s}`} className="pill bg-primary/10 text-primary text-[10px]">{s}</span>)}
              {app.languages?.map((l) => <span key={`l-${l}`} className="pill bg-secondary/10 text-secondary text-[10px]">{l}</span>)}
            </div>
          )}
          <div className="text-xs text-[#7A7D96] mt-3">Submitted {fmtDateTime(app.created_at)}</div>
        </div>
        <div className="flex md:flex-col gap-2 md:min-w-[160px]">
          <button onClick={onReview} className="flex-1 px-4 py-2.5 rounded-full bg-primary text-white font-bold text-sm shadow-md hover:-translate-y-0.5 transition-all" data-testid={`admin-app-review-${app.application_id}`}>
            {app.status === "pending" ? "Review" : "View"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewApplicationModal({ app, onClose, onReviewed }) {
  const [notes, setNotes] = useState(app.review_notes || "");
  const [busy, setBusy] = useState(false);

  const decide = async (action) => {
    if (busy) return;
    if (app.status !== "pending") return;
    if (action === "reject" && !notes.trim()) {
      toast.error("Please share a short reason (sent to the applicant).");
      return;
    }
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} this application?${action === "approve" ? "\nAn email will be sent with a password-set link." : "\nThe applicant will be notified by email."}`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/applications/${app.application_id}/${action}`, { review_notes: notes.trim() || undefined });
      toast.success(action === "approve" ? "Approved — welcome email sent" : "Rejected — email sent");
      onReviewed?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
      logger.warn("application review failed:", err?.message);
    } finally { setBusy(false); }
  };

  const downloadCv = async () => {
    try {
      const { data } = await api.get(`/admin/applications/${app.application_id}/cv`);
      const link = document.createElement("a");
      link.href = data.cv_document;
      link.download = data.cv_filename || "cv.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No CV attached");
    }
  };

  const bodyRegisterUrl = {
    HPCSA: "https://www.hpcsa.co.za/?contentId=132&menuSubId=32&actionName=Core%20Operations",
    SACSSP: "https://www.sacssp.co.za/",
    ASCHP: "https://aschp.net/",
  }[app.professional_body];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#3D405B]/40 backdrop-blur-sm" onClick={onClose} data-testid="admin-review-app-modal">
      <div className="brand-card p-7 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Application</div>
            <h2 className="text-2xl font-extrabold text-[#3D405B]">{app.name}</h2>
            <div className="text-sm text-[#5F627D] mt-1">Submitted {fmtDateTime(app.created_at)}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#3D405B]/5" aria-label="Close" data-testid="admin-review-close-btn"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-5">
          <div><div className="overline mb-1">Email</div><div className="font-bold text-[#3D405B]">{app.email}</div></div>
          <div><div className="overline mb-1">Phone</div><div className="text-[#3D405B]">{app.phone || "—"}</div></div>
          <div><div className="overline mb-1">City</div><div className="text-[#3D405B]">{app.city || "—"}</div></div>
          <div><div className="overline mb-1">Years experience</div><div className="text-[#3D405B]">{app.years_experience ?? "—"}</div></div>
          <div>
            <div className="overline mb-1">Registration</div>
            <div className="text-[#3D405B]">
              <strong>{app.professional_body}</strong> · {app.registration_number}
              {bodyRegisterUrl && (
                <a href={bodyRegisterUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-primary font-bold text-xs underline" data-testid="admin-review-register-link">verify →</a>
              )}
            </div>
          </div>
          <div><div className="overline mb-1">Practice mode</div><div className="text-[#3D405B] capitalize">{app.practice_mode?.replace("_", " ")}</div></div>
          {app.office_address && (
            <div className="sm:col-span-2"><div className="overline mb-1">Office address</div><div className="text-[#3D405B]">{app.office_address}</div></div>
          )}
          {app.languages?.length > 0 && (
            <div className="sm:col-span-2">
              <div className="overline mb-1">Languages</div>
              <div className="flex flex-wrap gap-1.5">{app.languages.map((l) => <span key={l} className="pill bg-secondary/10 text-secondary">{l}</span>)}</div>
            </div>
          )}
          {app.specialties?.length > 0 && (
            <div className="sm:col-span-2">
              <div className="overline mb-1">Specialties</div>
              <div className="flex flex-wrap gap-1.5">{app.specialties.map((s) => <span key={s} className="pill bg-primary/10 text-primary">{s}</span>)}</div>
            </div>
          )}
          {app.bio && (
            <div className="sm:col-span-2"><div className="overline mb-1">Bio</div><div className="text-[#3D405B] leading-relaxed whitespace-pre-line">{app.bio}</div></div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={downloadCv} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#3D405B]/15 text-[#3D405B] font-bold text-sm hover:bg-[#3D405B]/5" data-testid="admin-review-cv-btn">
            <FileDown size={14} /> Download CV
          </button>
          {bodyRegisterUrl && (
            <a href={bodyRegisterUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#3D405B]/15 text-[#3D405B] font-bold text-sm hover:bg-[#3D405B]/5">
              Open {app.professional_body} register
            </a>
          )}
        </div>

        {app.status === "pending" ? (
          <div className="pt-4 border-t border-[#3D405B]/5">
            <label className="text-xs font-bold uppercase tracking-[0.16em] text-[#3D405B] mb-1.5 block">
              Notes (sent to the applicant if rejecting)
            </label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional for approval · required for rejection" className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none text-sm resize-none mb-4" data-testid="admin-review-notes" />
            <div className="flex flex-wrap gap-2 justify-end">
              <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-[#3D405B]/15 font-bold text-[#3D405B] hover:bg-[#3D405B]/5">Close</button>
              <button type="button" onClick={() => decide("reject")} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-destructive text-white font-bold shadow-md disabled:opacity-50 hover:-translate-y-0.5 transition-all" data-testid="admin-review-reject-btn">
                <XCircle size={15} /> Reject
              </button>
              <button type="button" onClick={() => decide("approve")} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-bold shadow-md disabled:opacity-50 hover:-translate-y-0.5 transition-all" data-testid="admin-review-approve-btn">
                <Check size={15} /> Approve
              </button>
            </div>
          </div>
        ) : (
          <div className="pt-4 border-t border-[#3D405B]/5 text-sm text-[#5F627D]">
            <div className="mb-1"><strong>Status:</strong> {app.status}</div>
            {app.reviewed_at && <div className="mb-1"><strong>Reviewed:</strong> {fmtDateTime(app.reviewed_at)}</div>}
            {app.review_notes && <div className="mt-2 p-3 rounded-2xl bg-muted text-[#3D405B]"><strong>Notes:</strong> {app.review_notes}</div>}
          </div>
        )}
      </div>
    </div>
  );
}


const ACTION_LABEL = {
  "user.update": "Edited user",
  "user.delete": "Deleted user",
  "user.toggle_active": "Toggled active",
  "counsellor.create": "Created counsellor",
  "application.approve": "Approved application",
  "application.reject": "Rejected application",
  "resource.create": "Added self-help video",
  "resource.delete": "Removed self-help video",
};

const ACTION_TONE = {
  "user.delete": "bg-destructive/10 text-destructive border-destructive/20",
  "application.reject": "bg-destructive/10 text-destructive border-destructive/20",
  "resource.delete": "bg-destructive/10 text-destructive border-destructive/20",
  "application.approve": "bg-secondary/15 text-secondary border-secondary/30",
  "counsellor.create": "bg-secondary/15 text-secondary border-secondary/30",
  "resource.create": "bg-primary/10 text-primary border-primary/20",
  "user.update": "bg-accent/25 text-[#3D405B] border-accent/50",
  "user.toggle_active": "bg-accent/25 text-[#3D405B] border-accent/50",
};

function AuditLogTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (actionFilter) params.set("action", actionFilter);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/audit-log?${params.toString()}`);
      setEntries(data);
    } catch (e) {
      toast.error("Could not load audit log");
      logger.warn("audit load failed:", e?.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [actionFilter, q]);

  const onSearch = (e) => { e.preventDefault(); setQ(qInput.trim()); };

  return (
    <div data-testid="admin-audit-log">
      <div className="brand-card p-5 mb-5 bg-accent/15 text-[#3D405B] text-sm flex items-start gap-3">
        <ShieldCheck size={18} className="text-secondary shrink-0 mt-0.5" />
        <div>
          <strong>POPIA audit trail.</strong> Every admin action here is recorded — who did it, when, and what changed. Read-only. Retention is indefinite by design.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white border border-[#3D405B]/15">
          <Filter size={14} className="text-[#7A7D96]" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="bg-transparent text-sm font-bold text-[#3D405B] outline-none py-1.5 pr-2"
            data-testid="audit-filter-action"
          >
            <option value="">All actions</option>
            {Object.entries(ACTION_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        <form onSubmit={onSearch} className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white border border-[#3D405B]/15">
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search actor or target email"
            className="bg-transparent text-sm outline-none py-1.5 min-w-[220px]"
            data-testid="audit-filter-q"
          />
          <button type="submit" className="px-3 py-1.5 rounded-full bg-primary text-white text-xs font-bold" data-testid="audit-search-btn">Search</button>
          {q && (
            <button type="button" onClick={() => { setQInput(""); setQ(""); }} className="px-2 py-1.5 text-[#7A7D96] hover:text-[#3D405B]" aria-label="Clear search" data-testid="audit-search-clear">
              <X size={14} />
            </button>
          )}
        </form>

        <span className="ml-auto text-sm text-[#7A7D96]">{entries.length} entr{entries.length === 1 ? "y" : "ies"}</span>
      </div>

      {loading ? (
        <div className="brand-card p-10 text-center text-[#7A7D96]">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="brand-card p-10 text-center text-[#7A7D96]">No audit entries match this filter yet.</div>
      ) : (
        <div className="brand-card overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 bg-muted text-xs font-bold uppercase tracking-wider text-[#7A7D96]">
            <div className="col-span-2">When</div>
            <div className="col-span-3">Who</div>
            <div className="col-span-3">Action</div>
            <div className="col-span-4">Target · changes</div>
          </div>
          {entries.map((e) => (
            <AuditRow key={e.audit_id} entry={e} />
          ))}
        </div>
      )}
    </div>
  );
}

function AuditRow({ entry }) {
  const [open, setOpen] = useState(false);
  const label = ACTION_LABEL[entry.action] || entry.action;
  const tone = ACTION_TONE[entry.action] || "bg-muted text-[#3D405B] border-[#3D405B]/10";
  const hasDetails = !!(entry.changes || entry.meta);
  return (
    <div className="border-t border-[#3D405B]/5" data-testid={`audit-row-${entry.audit_id}`}>
      <button
        type="button"
        onClick={() => hasDetails && setOpen((v) => !v)}
        className={`w-full grid grid-cols-12 px-5 py-4 items-center text-sm text-left ${hasDetails ? "hover:bg-[#3D405B]/[.02] cursor-pointer" : "cursor-default"}`}
      >
        <div className="col-span-2 text-[#5F627D] text-xs">{fmtDateTime(entry.created_at)}</div>
        <div className="col-span-3 min-w-0">
          <div className="font-bold text-[#3D405B] truncate">{entry.actor_name || entry.actor_email || entry.actor_id}</div>
          {entry.actor_email && entry.actor_name && (
            <div className="text-[11px] text-[#7A7D96] truncate">{entry.actor_email}</div>
          )}
        </div>
        <div className="col-span-3">
          <span className={`pill border ${tone}`}>{label}</span>
        </div>
        <div className="col-span-4 min-w-0">
          <div className="text-[#3D405B] truncate">
            {entry.target_label ? (
              <span className="inline-flex items-center gap-1"><ArrowRight size={12} className="text-[#7A7D96]" /> {entry.target_label}</span>
            ) : (
              <span className="text-[#7A7D96] italic">—</span>
            )}
          </div>
          {entry.changes && (
            <div className="text-[11px] text-[#7A7D96] truncate">
              {Object.keys(entry.changes).slice(0, 3).join(", ")}
              {Object.keys(entry.changes).length > 3 ? "…" : ""}
            </div>
          )}
        </div>
      </button>
      {open && hasDetails && (
        <div className="px-5 pb-4 -mt-1 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          {entry.changes && (
            <div className="brand-card p-3 bg-muted">
              <div className="overline mb-2">Changes</div>
              <div className="space-y-1.5">
                {Object.entries(entry.changes).map(([field, { before, after }]) => (
                  <div key={field}>
                    <div className="font-bold text-[#3D405B]">{field}</div>
                    <div className="text-[#7A7D96] flex items-start gap-2">
                      <span className="line-through opacity-70 break-all">{formatVal(before)}</span>
                      <ArrowRight size={11} className="mt-1 shrink-0" />
                      <span className="text-[#3D405B] font-bold break-all">{formatVal(after)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {entry.meta && Object.keys(entry.meta).length > 0 && (
            <div className="brand-card p-3 bg-muted">
              <div className="overline mb-2">Context</div>
              <pre className="text-[#3D405B] whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed">{JSON.stringify(entry.meta, null, 2)}</pre>
            </div>
          )}
          {(entry.ip || entry.user_agent) && (
            <div className="md:col-span-2 text-[10px] text-[#7A7D96]">
              {entry.ip && <>IP: <span className="font-mono">{entry.ip}</span> · </>}
              {entry.user_agent && <>UA: <span className="font-mono truncate">{entry.user_agent.slice(0, 100)}</span></>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}


const PURPOSE_LABEL = {
  booking_confirmation: "Booking confirmation",
  session_reminder: "Session reminder",
  password_reset: "Password reset",
  parent_link_request: "Parent link request",
  admin_notify: "Admin notification",
  admin_daily_digest: "Admin daily digest",
  counsellor_application_received: "Counsellor: application received",
  counsellor_approved: "Counsellor: approved",
  counsellor_rejected: "Counsellor: rejected",
  offline_chat_notification: "Offline chat notification",
  generic: "Generic",
};

const STATUS_TONE = {
  sent: "bg-secondary/15 text-secondary border-secondary/30",
  failed: "bg-destructive/10 text-destructive border-destructive/20",
  mocked: "bg-accent/30 text-[#3D405B] border-accent/50",
};

function EmailLogTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purposeFilter, setPurposeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (purposeFilter) params.set("purpose", purposeFilter);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/admin/email-log?${params.toString()}`);
      setRows(data);
    } catch (e) {
      toast.error("Could not load email log");
      logger.warn("email log load failed:", e?.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [purposeFilter, statusFilter]);

  return (
    <div data-testid="admin-email-log">
      <div className="brand-card p-5 mb-5 bg-accent/15 text-[#3D405B] text-sm flex items-start gap-3">
        <Send size={18} className="text-secondary shrink-0 mt-0.5" />
        <div>
          <strong>Last 50 transactional emails.</strong> Every send attempt (confirmations, reminders, digests, chat pings, application updates) is logged here with its Resend message id — use it to answer &ldquo;did the email actually go out?&rdquo; support tickets in seconds.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white border border-[#3D405B]/15">
          <Filter size={14} className="text-[#7A7D96]" />
          <select
            value={purposeFilter}
            onChange={(e) => setPurposeFilter(e.target.value)}
            className="bg-transparent text-sm font-bold text-[#3D405B] outline-none py-1.5 pr-2"
            data-testid="email-filter-purpose"
          >
            <option value="">All purposes</option>
            {Object.entries(PURPOSE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        <div className="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-white border border-[#3D405B]/15">
          <Filter size={14} className="text-[#7A7D96]" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-transparent text-sm font-bold text-[#3D405B] outline-none py-1.5 pr-2"
            data-testid="email-filter-status"
          >
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="mocked">Mocked (no API key)</option>
          </select>
        </div>

        <button onClick={load} className="px-3 py-1.5 rounded-full bg-white border border-[#3D405B]/15 text-xs font-bold text-[#3D405B] inline-flex items-center gap-1" data-testid="email-refresh-btn">
          <History size={12} /> Refresh
        </button>

        <span className="ml-auto text-sm text-[#7A7D96]">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <div className="brand-card p-10 text-center text-[#7A7D96]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="brand-card p-10 text-center text-[#7A7D96]">No email attempts logged yet.</div>
      ) : (
        <div className="brand-card overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 bg-muted text-xs font-bold uppercase tracking-wider text-[#7A7D96]">
            <div className="col-span-2">When</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Purpose</div>
            <div className="col-span-3">Recipient</div>
            <div className="col-span-3">Subject · id</div>
          </div>
          {rows.map((r, i) => (
            <div key={r.resend_id || `${r.to}-${r.created_at}-${i}`} className="grid grid-cols-12 items-start px-5 py-3 border-t border-[#3D405B]/5 text-sm" data-testid={`email-row-${i}`}>
              <div className="col-span-2 text-[#7A7D96]">{fmtDateTime(r.created_at)}</div>
              <div className="col-span-2">
                <span className={`pill inline-flex items-center gap-1 ${STATUS_TONE[r.status] || "bg-muted text-[#3D405B]"}`}>
                  {r.status === "sent" && <Check size={11} />}
                  {r.status === "failed" && <XCircle size={11} />}
                  {r.status === "mocked" && <Mail size={11} />}
                  {r.status}
                </span>
              </div>
              <div className="col-span-2 text-[#3D405B] font-semibold">{PURPOSE_LABEL[r.purpose] || r.purpose || "—"}</div>
              <div className="col-span-3 text-[#3D405B] break-all">{r.to}</div>
              <div className="col-span-3 text-[#5F627D]">
                <div className="truncate" title={r.subject}>{r.subject}</div>
                {r.resend_id && <div className="text-[10px] font-mono text-[#7A7D96] mt-0.5 truncate" title={r.resend_id}>id: {r.resend_id}</div>}
                {r.error && <div className="text-[11px] text-destructive mt-0.5 line-clamp-2" title={r.error}>{r.error}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function monthLabel(iso) {
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return iso; }
}

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function ReportSection({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="brand-card p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-2xl bg-secondary/15 text-secondary flex items-center justify-center shrink-0">
          <Icon size={18} />
        </div>
        <div>
          <h3 className="text-lg font-extrabold text-[#3D405B]">{title}</h3>
          {subtitle && <p className="text-sm text-[#7A7D96]">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function GeoReport({ role, testid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/geo?role=${role}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load geo report"))
      .finally(() => setLoading(false));
  }, [role]);

  if (loading) return <div className="text-sm text-[#7A7D96]">Loading…</div>;
  if (!data || data.rows.length === 0) return <div className="text-sm text-[#7A7D96]">No data yet.</div>;

  const grouped = {};
  data.rows.forEach((r) => {
    if (!grouped[r.province]) grouped[r.province] = { total: 0, cities: {} };
    grouped[r.province].total += r.count;
    const cityKey = r.city;
    if (!grouped[r.province].cities[cityKey]) grouped[r.province].cities[cityKey] = { total: 0, suburbs: [] };
    grouped[r.province].cities[cityKey].total += r.count;
    grouped[r.province].cities[cityKey].suburbs.push({ suburb: r.suburb, count: r.count });
  });

  return (
    <div data-testid={testid}>
      <div className="text-sm text-[#7A7D96] mb-3">
        Total <span className="font-extrabold text-[#3D405B]">{data.total}</span> {role}
        {data.total === 1 ? "" : "s"} across <span className="font-extrabold text-[#3D405B]">{Object.keys(grouped).length}</span> province
        {Object.keys(grouped).length === 1 ? "" : "s"}.
      </div>
      <div className="space-y-3">
        {Object.entries(grouped)
          .sort(([, a], [, b]) => b.total - a.total)
          .map(([province, pdata]) => (
            <details key={province} className="rounded-2xl border border-[#3D405B]/10 overflow-hidden" open={province !== "Unknown"}>
              <summary className="cursor-pointer px-4 py-3 bg-muted flex items-center justify-between hover:bg-[#3D405B]/5">
                <span className="font-extrabold text-[#3D405B]">{province}</span>
                <span className="pill bg-secondary/15 text-secondary">{pdata.total}</span>
              </summary>
              <div className="p-4 space-y-2">
                {Object.entries(pdata.cities)
                  .sort(([, a], [, b]) => b.total - a.total)
                  .map(([city, cdata]) => (
                    <div key={city} className="pl-2 border-l-2 border-secondary/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-sm text-[#3D405B]">{city}</span>
                        <span className="text-xs font-bold text-[#7A7D96]">{cdata.total}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 pl-2">
                        {cdata.suburbs
                          .sort((a, b) => b.count - a.count)
                          .map((s) => (
                            <span key={s.suburb || "Unknown"} className="pill bg-white border border-[#3D405B]/15 text-[#5F627D] text-[11px]">
                              {s.suburb} · <span className="font-extrabold text-[#3D405B]">{s.count}</span>
                            </span>
                          ))}
                      </div>
                    </div>
                  ))}
              </div>
            </details>
          ))}
      </div>
    </div>
  );
}

function StatTile({ label, value, sublabel, tone = "primary" }) {
  const toneMap = {
    primary: "bg-primary/10 text-primary border-primary/20",
    secondary: "bg-secondary/15 text-secondary border-secondary/25",
    destructive: "bg-destructive/10 text-destructive border-destructive/20",
    muted: "bg-muted text-[#3D405B] border-[#3D405B]/10",
  };
  return (
    <div className={`rounded-2xl p-4 border ${toneMap[tone]}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-3xl font-extrabold mt-1">{value}</div>
      {sublabel && <div className="text-[11px] font-semibold opacity-70 mt-0.5">{sublabel}</div>}
    </div>
  );
}

function BookingsReport() {
  const [month, setMonth] = useState(currentMonthValue());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/bookings?month=${month}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load bookings report"))
      .finally(() => setLoading(false));
  }, [month]);

  const maxDaily = data ? Math.max(1, ...data.daily.map((d) => d.created)) : 1;

  return (
    <div data-testid="report-bookings">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="text-xs font-bold uppercase tracking-wider text-[#7A7D96]">Month</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-2 rounded-full bg-white border border-[#3D405B]/15 text-sm font-bold text-[#3D405B] outline-none focus:border-secondary"
          data-testid="report-bookings-month"
        />
      </div>
      {loading ? (
        <div className="text-sm text-[#7A7D96]">Loading…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatTile label="Total bookings" value={data.total} tone="primary" />
            <StatTile label="Completed" value={data.completed} sublabel={`${data.completion_rate}% completion`} tone="secondary" />
            <StatTile label="Cancelled" value={data.cancelled} sublabel={`${data.cancellation_rate}% cancel rate`} tone="destructive" />
            <StatTile label="Still scheduled" value={data.scheduled} tone="muted" />
          </div>

          {data.daily.length === 0 ? (
            <div className="text-sm text-[#7A7D96]">No activity in {data.month}.</div>
          ) : (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-2">Daily activity — {data.month}</div>
              <div className="space-y-1.5">
                {data.daily.map((d) => (
                  <div key={d.day} className="flex items-center gap-3 text-xs">
                    <div className="w-14 text-[#7A7D96] font-semibold">{monthLabel(d.day)}</div>
                    <div className="flex-1 flex items-center gap-1">
                      <div className="h-5 bg-primary/70 rounded-l" style={{ width: `${(d.created / maxDaily) * 60}%` }} title={`${d.created} created`} />
                      {d.completed > 0 && <div className="h-5 bg-secondary/70" style={{ width: `${(d.completed / maxDaily) * 60}%` }} title={`${d.completed} completed`} />}
                      {d.cancelled > 0 && <div className="h-5 bg-destructive/70 rounded-r" style={{ width: `${(d.cancelled / maxDaily) * 60}%` }} title={`${d.cancelled} cancelled`} />}
                    </div>
                    <div className="w-24 text-right text-[#3D405B] font-bold">
                      {d.created}c · {d.completed}✓ · {d.cancelled}✕
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 text-[11px] text-[#7A7D96] mt-4">
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-primary/70 inline-block" /> Created</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-secondary/70 inline-block" /> Completed</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-destructive/70 inline-block" /> Cancelled</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LoginsReport() {
  const [month, setMonth] = useState(currentMonthValue());
  const [role, setRole] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const roleQ = role === "all" ? "" : `&role=${role}`;
    api.get(`/admin/reports/logins?month=${month}${roleQ}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load logins report"))
      .finally(() => setLoading(false));
  }, [month, role]);

  const maxDaily = data ? Math.max(1, ...data.daily.map((d) => d.count)) : 1;

  return (
    <div data-testid="report-logins">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="text-xs font-bold uppercase tracking-wider text-[#7A7D96]">Month</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-2 rounded-full bg-white border border-[#3D405B]/15 text-sm font-bold text-[#3D405B] outline-none focus:border-secondary"
          data-testid="report-logins-month"
        />
        <div className="flex gap-1.5 flex-wrap">
          {["all", "client", "counsellor", "parent", "admin"].map((r) => (
            <button
              key={r}
              onClick={() => setRole(r)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-colors ${role === r ? "bg-primary text-white" : "bg-white border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"}`}
              data-testid={`logins-role-${r}`}
            >
              {r === "all" ? "All roles" : `${r}s`}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-[#7A7D96]">Loading…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatTile label="Total logins" value={data.total} tone="primary" />
            <StatTile label="Unique users" value={data.unique_users} tone="secondary" />
            <StatTile label="Clients" value={data.by_role.client} sublabel={`${data.by_role.parent} parents`} tone="muted" />
            <StatTile label="Counsellors" value={data.by_role.counsellor} sublabel={`${data.by_role.admin} admins`} tone="muted" />
          </div>

          {data.daily.length === 0 ? (
            <div className="text-sm text-[#7A7D96]">No logins recorded in {data.month}{role !== "all" ? ` for ${role}s` : ""}.</div>
          ) : (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-2">Daily logins — {data.month}</div>
              <div className="space-y-1.5">
                {data.daily.map((d) => (
                  <div key={d.day} className="flex items-center gap-3 text-xs">
                    <div className="w-14 text-[#7A7D96] font-semibold">{monthLabel(d.day)}</div>
                    <div className="flex-1">
                      <div className="h-5 bg-secondary/70 rounded" style={{ width: `${(d.count / maxDaily) * 90}%` }} />
                    </div>
                    <div className="w-16 text-right text-[#3D405B] font-bold">{d.count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ReportsTab() {
  const [params, setParams] = useSearchParams();
  const sub = params.get("sub") || "upcoming";
  const exportCsv = () => {
    const base = api.defaults.baseURL || "";
    const a = document.createElement("a");
    a.href = `${base}/admin/export/users.csv`;
    a.rel = "noopener";
    a.click();
  };

  // Map each sub-report id to its rendered component + human title/subtitle so the
  // sub-nav stays declarative and adding a new report is a 3-line change.
  const REPORTS = {
    upcoming: { title: "Upcoming bookings", subtitle: "All confirmed future sessions with client + counsellor contact details.", component: <UpcomingBookingsReport /> },
    new: { title: "New bookings", subtitle: "Bookings created in the selected window, newest first.", component: <NewBookingsReport /> },
    roster: { title: "Roster (next 7 days)", subtitle: "Confirmed + pending bookings soonest first — with one-tap email / WhatsApp share.", component: <RosterReport /> },
    past: { title: "Past bookings (last 7 days)", subtitle: "Detailed operational log with wait times, actual duration, summary and outcome.", component: <PastBookingsReport /> },
    attendance: { title: "Attendance & outcomes", subtitle: "Acceptances, no-shows and counsellor-recorded outcomes.", component: <AttendanceReport /> },
    utilization: { title: "Counsellor utilization", subtitle: "Published slots vs booked — spot burnout at 80%+ and under-use below 25%.", component: <UtilizationReport /> },
    notes: { title: "Notes compliance", subtitle: "% of completed sessions with a DAP case-note filed within 48h.", component: <NotesComplianceReport /> },
    contribution: { title: "Contribution & appreciation", subtitle: "Hours donated, sessions and unique clients — used to generate appreciation letters for our counsellors.", component: <ContributionReport /> },
    impact: { title: "Impact dashboard", subtitle: "Lives touched, geographic reach and demographics — perfect for grant applications.", component: <ImpactReport /> },
    wellness: { title: "Wellness outcomes", subtitle: "Pre/post wellness score deltas — quantified evidence of positive change.", component: <WellnessOutcomesReport /> },
    funnel: { title: "Client acquisition funnel", subtitle: "Signup → first booking → completed → repeat, with conversion at each stage.", component: <FunnelReport /> },
    sla: { title: "Response times / SLAs", subtitle: "How fast counsellors accept + start sessions — median and P90.", component: <ResponseTimesReport /> },
    risk: { title: "Risk & escalation", subtitle: "Flagged case-notes and low wellness check-ins that may need supervisor attention.", component: <RiskRegisterReport /> },
    donations: { title: "Donations ledger", subtitle: "Yoco donations + sponsored sessions, rolled up by campaign.", component: <DonationsReport /> },
    geo_clients: { title: "Clients by geography", subtitle: "Where the youth using the platform live — 'Unknown' rows highlight profile-completion gaps.", component: <GeoReport role="client" testid="report-geo-clients" /> },
    geo_counsellors: { title: "Counsellors by geography", subtitle: "National coverage — spot underserved provinces for recruitment drives.", component: <GeoReport role="counsellor" testid="report-geo-counsellors" /> },
    bookings: { title: "Bookings — monthly", subtitle: "Created / completed / cancelled by month. Completion rate is the north-star metric.", component: <BookingsReport /> },
    logins: { title: "Logins — monthly", subtitle: "Login volume by role — the engagement signal for spotting drop-off.", component: <LoginsReport /> },
    availability: { title: "Counsellor availability coverage", subtitle: "Who has open slots in the next 14 days? Empty calendars get auto-nudged every Monday.", component: <AvailabilityReport /> },
  };

  // Extend the sidebar groups above with the operational reports at the bottom of the list.
  const groupsForRender = [
    ...REPORT_GROUPS,
    {
      label: "Operations",
      items: [
        { id: "bookings", label: "Bookings — monthly", icon: TrendingUp },
        { id: "logins", label: "Logins — monthly", icon: LogIn },
        { id: "availability", label: "Availability coverage", icon: CalendarCheck },
        { id: "geo_clients", label: "Clients geography", icon: MapPin },
        { id: "geo_counsellors", label: "Counsellors geography", icon: ShieldCheck },
      ],
    },
  ];

  const active = REPORTS[sub] || REPORTS.upcoming;

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-[#3D405B]">Reports</h1>
          <p className="text-sm text-[#7A7D96] mt-1">Every number you need to run and grow Hello Foundation.</p>
        </div>
        <button
          onClick={exportCsv}
          className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-white text-sm font-extrabold shadow-sm hover:shadow-md transition-all"
          data-testid="admin-export-users-csv"
        >
          <FileDown size={14} /> Export contacts
        </button>
      </div>

      {/* Sub-nav pills — grouped horizontally. Wraps on narrow viewports. */}
      <div className="brand-card p-3 space-y-3" data-testid="reports-subnav">
        {groupsForRender.map((g, gi) => (
          <div key={gi} className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase font-extrabold tracking-[0.18em] text-[#7A7D96] w-full sm:w-32 shrink-0">{g.label}</span>
            <div className="flex flex-wrap gap-1.5">
              {g.items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => setParams({ tab: "reports", sub: it.id })}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold transition-all ${sub === it.id ? "bg-primary text-white shadow-sm" : "bg-muted/40 text-[#3D405B] hover:bg-[#3D405B]/10"}`}
                  data-testid={`report-tab-${it.id}`}
                >
                  <it.icon size={12} /> {it.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <ReportSection icon={BarChart3} title={active.title} subtitle={active.subtitle}>
        {active.component}
      </ReportSection>
    </div>
  );
}



function CommunicationTab() {
  const [lists, setLists] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [listId, setListId] = useState("all");
  const [channels, setChannels] = useState(["email"]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: L }, { data: H }] = await Promise.all([
        api.get("/admin/distribution-lists"),
        api.get("/admin/broadcasts?limit=50"),
      ]);
      setLists(L);
      setHistory(H);
    } catch {
      toast.error("Could not load communication data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const selectedList = useMemo(() => lists.find((l) => l.id === listId) || null, [lists, listId]);

  const targetCount = useMemo(() => {
    if (!selectedList) return { email: 0, whatsapp: 0 };
    return {
      email: channels.includes("email") ? selectedList.count_email : 0,
      whatsapp: channels.includes("whatsapp") ? selectedList.count_whatsapp : 0,
    };
  }, [selectedList, channels]);

  const toggleChannel = (ch) => {
    setChannels((prev) => (prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]));
  };

  const send = async () => {
    if (!body.trim()) { toast.error("Message body is required"); return; }
    if (channels.length === 0) { toast.error("Pick at least one channel"); return; }
    if (channels.includes("email") && !subject.trim()) { toast.error("Subject is required for email"); return; }

    const total = targetCount.email + targetCount.whatsapp;
    const label = selectedList?.label || listId;
    if (!window.confirm(`Send this message to ${label} — ${total} deliveries across ${channels.join(" + ")}?\n\nThis cannot be undone.`)) return;

    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcasts", {
        list_id: listId,
        channels,
        subject: channels.includes("email") ? subject : null,
        body,
      });
      const s = data.stats;
      toast.success(`Sent · email ${s.email.sent}/${s.email.sent + s.email.failed} · whatsapp ${s.whatsapp.sent}/${s.whatsapp.sent + s.whatsapp.failed}`);
      setBody(""); setSubject("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const CHIP = {
    all: "bg-primary/10 text-primary border-primary/20",
    client: "bg-secondary/15 text-secondary border-secondary/25",
    counsellor: "bg-accent/40 text-[#3D405B] border-accent",
    parent: "bg-muted text-[#3D405B] border-[#3D405B]/10",
    admin: "bg-[#3D405B]/10 text-[#3D405B] border-[#3D405B]/20",
  };

  return (
    <div className="space-y-6" data-testid="admin-communication">
      <div className="brand-card p-5 bg-accent/15 text-[#3D405B] text-sm flex items-start gap-3">
        <Megaphone size={18} className="text-secondary shrink-0 mt-0.5" />
        <div>
          <strong>Broadcast centre.</strong> Send announcements to a distribution list via email, WhatsApp, or both. WhatsApp only reaches users who have opted in and have a phone on file — the counts on each list are live.
        </div>
      </div>

      {loading ? (
        <div className="brand-card p-10 text-center text-[#7A7D96]">Loading…</div>
      ) : (
        <>
          {/* Compose card */}
          <div className="brand-card p-6 space-y-5" data-testid="broadcast-compose">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Send size={18} />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-[#3D405B]">New broadcast</h3>
                <p className="text-sm text-[#7A7D96]">Compose once — reach everyone on the selected list.</p>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-2 block">Distribution list</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {lists.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setListId(l.id)}
                    className={`text-left rounded-2xl border-2 p-3 transition-all ${listId === l.id ? "border-primary bg-primary/5 shadow-sm" : "border-[#3D405B]/10 bg-white hover:border-[#3D405B]/25"}`}
                    data-testid={`broadcast-list-${l.id}`}
                  >
                    <div className="text-xs font-extrabold text-[#3D405B]">{l.label}</div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="pill bg-secondary/15 text-secondary text-[10px] inline-flex items-center gap-0.5"><Mail size={10} /> {l.count_email}</span>
                      <span className="pill bg-primary/10 text-primary text-[10px] inline-flex items-center gap-0.5"><MessageCircle size={10} /> {l.count_whatsapp}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-2 block">Channels</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => toggleChannel("email")}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${channels.includes("email") ? "bg-secondary text-white border-secondary" : "bg-white text-[#3D405B] border-[#3D405B]/15 hover:border-secondary"}`}
                  data-testid="broadcast-channel-email"
                >
                  {channels.includes("email") && <Check size={14} />} <Mail size={14} /> Email
                  <span className="text-[11px] opacity-80">· {selectedList?.count_email ?? 0}</span>
                </button>
                <button
                  onClick={() => toggleChannel("whatsapp")}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${channels.includes("whatsapp") ? "bg-primary text-white border-primary" : "bg-white text-[#3D405B] border-[#3D405B]/15 hover:border-primary"}`}
                  data-testid="broadcast-channel-whatsapp"
                >
                  {channels.includes("whatsapp") && <Check size={14} />} <MessageCircle size={14} /> WhatsApp
                  <span className="text-[11px] opacity-80">· {selectedList?.count_whatsapp ?? 0}</span>
                </button>
              </div>
            </div>

            {channels.includes("email") && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-2 block">Subject <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-full bg-white border border-[#3D405B]/15 text-sm text-[#3D405B] outline-none focus:border-primary"
                  placeholder="e.g. Important service update"
                  data-testid="broadcast-subject"
                />
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-2 block">Message body <span className="text-destructive">*</span></label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 text-sm text-[#3D405B] outline-none focus:border-primary resize-y"
                placeholder="Write your message here… Keep it short for WhatsApp — the same text is used for both channels."
                data-testid="broadcast-body"
              />
              <div className="text-[11px] text-[#7A7D96] mt-1 flex items-center justify-between">
                <span>{body.length} characters</span>
                {body.length > 1000 && <span className="text-amber-700 font-bold">Long messages may be rejected by WhatsApp; consider trimming.</span>}
              </div>
            </div>

            <div className="pt-3 border-t border-[#3D405B]/10 flex flex-wrap items-center gap-3">
              <div className="text-sm text-[#5F627D]">
                Will send to <span className="font-extrabold text-[#3D405B]" data-testid="broadcast-target-count">{targetCount.email + targetCount.whatsapp}</span> recipient
                {(targetCount.email + targetCount.whatsapp) === 1 ? "" : "s"}
                {channels.includes("email") && <> — <span className="font-bold">{targetCount.email}</span> email</>}
                {channels.includes("whatsapp") && <> — <span className="font-bold">{targetCount.whatsapp}</span> WhatsApp</>}
              </div>
              <button
                onClick={send}
                disabled={sending || (targetCount.email + targetCount.whatsapp) === 0 || !body.trim()}
                className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-extrabold shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="broadcast-send-btn"
              >
                <Send size={16} /> {sending ? "Sending…" : "Send now"}
              </button>
            </div>
          </div>

          {/* History */}
          <div className="brand-card overflow-hidden" data-testid="broadcast-history">
            <div className="px-5 py-4 border-b border-[#3D405B]/5 flex items-center justify-between">
              <div>
                <h3 className="text-base font-extrabold text-[#3D405B]">Recent broadcasts</h3>
                <p className="text-xs text-[#7A7D96]">Last {history.length} sends · newest first</p>
              </div>
              <button onClick={load} className="px-3 py-1.5 rounded-full bg-white border border-[#3D405B]/15 text-xs font-bold text-[#3D405B] inline-flex items-center gap-1" data-testid="broadcast-refresh">
                <History size={12} /> Refresh
              </button>
            </div>
            {history.length === 0 ? (
              <div className="p-10 text-center text-[#7A7D96]">No broadcasts sent yet — send your first one above.</div>
            ) : (
              <div className="divide-y divide-[#3D405B]/5">
                {history.map((b) => (
                  <div key={b.broadcast_id} className="p-5" data-testid={`broadcast-row-${b.broadcast_id}`}>
                    <div className="flex flex-wrap items-start gap-3 mb-2">
                      <span className={`pill border ${CHIP[b.list_id] || CHIP.all}`}>{lists.find((l) => l.id === b.list_id)?.label || b.list_id}</span>
                      {b.channels.map((c) => (
                        <span key={c} className="pill bg-white border border-[#3D405B]/15 text-[#3D405B] inline-flex items-center gap-1 capitalize">
                          {c === "email" ? <Mail size={11} /> : <MessageCircle size={11} />}
                          {c}
                        </span>
                      ))}
                      <span className="ml-auto text-xs text-[#7A7D96]">{fmtDateTime(b.created_at)} · by {b.sent_by_name}</span>
                    </div>
                    {b.subject && <div className="font-extrabold text-[#3D405B] text-sm mb-1">{b.subject}</div>}
                    <div className="text-sm text-[#5F627D] whitespace-pre-wrap line-clamp-3">{b.body}</div>
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                      {b.channels.includes("email") && (
                        <span className="pill bg-secondary/10 text-secondary">
                          Email: {b.stats.email.sent} sent · {b.stats.email.failed} failed · {b.stats.email.skipped} skipped
                        </span>
                      )}
                      {b.channels.includes("whatsapp") && (
                        <span className="pill bg-primary/10 text-primary">
                          WhatsApp: {b.stats.whatsapp.sent} sent · {b.stats.whatsapp.failed} failed · {b.stats.whatsapp.skipped} skipped
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}



function UpcomingBookingsReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysAhead, setDaysAhead] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/upcoming-bookings?days_ahead=${daysAhead}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load upcoming bookings"))
      .finally(() => setLoading(false));
  }, [daysAhead]);

  const fmtWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) return <div className="brand-card p-5 animate-pulse h-40" />;
  if (!data || data.total === 0) {
    return (
      <div className="brand-card p-6 text-center" data-testid="upcoming-bookings-empty">
        <CalendarClock size={22} className="text-secondary mx-auto mb-2" />
        <div className="text-sm font-extrabold text-[#3D405B]">No upcoming bookings in the next {data?.days_ahead || daysAhead} days</div>
        <div className="text-xs text-[#7A7D96] mt-1">When clients book, they&apos;ll appear here — newest scheduled first.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="report-upcoming-bookings">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#3D405B]">Window</label>
        <select
          value={daysAhead}
          onChange={(e) => setDaysAhead(Number(e.target.value))}
          className="text-sm rounded-xl border border-[#3D405B]/15 bg-white px-3 py-1.5 font-semibold text-[#3D405B]"
          data-testid="upcoming-bookings-window"
        >
          <option value={7}>Next 7 days</option>
          <option value={14}>Next 14 days</option>
          <option value={30}>Next 30 days</option>
          <option value={60}>Next 60 days</option>
          <option value={90}>Next 90 days</option>
        </select>
        <span className="pill bg-primary/10 text-primary" data-testid="upcoming-bookings-total">{data.total} sessions</span>
      </div>

      {data.grouped_by_day.map((group) => (
        <div key={group.day} className="brand-card p-5" data-testid={`upcoming-day-${group.day}`}>
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#3D405B]/10">
            <div className="font-extrabold text-[#3D405B]">
              {new Date(group.day + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <span className="pill bg-muted text-[#5F627D]">{group.count} session{group.count === 1 ? "" : "s"}</span>
          </div>
          <div className="space-y-3">
            {group.items.map((b) => (
              <div key={b.booking_id} className="grid grid-cols-1 md:grid-cols-12 gap-3 rounded-2xl border border-[#3D405B]/10 bg-white/70 p-3" data-testid={`upcoming-row-${b.booking_id}`}>
                <div className="md:col-span-2">
                  <div className="text-xs uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">When</div>
                  <div className="font-extrabold text-[#3D405B] text-sm">{fmtWhen(b.scheduled_at).split(",")[1] || fmtWhen(b.scheduled_at)}</div>
                  <div className="text-[11px] text-[#7A7D96] mt-1 inline-flex items-center gap-1">
                    {b.session_mode === "in_person" ? <MapPin size={11} /> : <Calendar size={11} />} {b.session_mode === "in_person" ? "In person" : "Online"}
                  </div>
                </div>

                <div className="md:col-span-4 md:border-l md:border-[#3D405B]/10 md:pl-4">
                  <div className="text-xs uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">Counsellor</div>
                  <div className="font-extrabold text-[#3D405B] text-sm truncate">{b.counsellor?.name || "—"}</div>
                  <div className="text-[11px] text-[#5F627D] mt-0.5 space-y-0.5">
                    {b.counsellor?.email && <div className="inline-flex items-center gap-1 truncate"><Mail size={11} /> <a href={`mailto:${b.counsellor.email}`} className="hover:text-primary truncate">{b.counsellor.email}</a></div>}
                    {b.counsellor?.phone && <div className="inline-flex items-center gap-1"><Phone size={11} /> <a href={`tel:${b.counsellor.phone}`} className="hover:text-primary">{b.counsellor.phone}</a></div>}
                  </div>
                </div>

                <div className="md:col-span-4 md:border-l md:border-[#3D405B]/10 md:pl-4">
                  <div className="text-xs uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">Client</div>
                  <div className="font-extrabold text-[#3D405B] text-sm truncate">{b.client?.name || "—"}</div>
                  <div className="text-[11px] text-[#5F627D] mt-0.5 space-y-0.5">
                    {b.client?.email && <div className="inline-flex items-center gap-1 truncate"><Mail size={11} /> <a href={`mailto:${b.client.email}`} className="hover:text-primary truncate">{b.client.email}</a></div>}
                    {b.client?.phone && <div className="inline-flex items-center gap-1"><Phone size={11} /> <a href={`tel:${b.client.phone}`} className="hover:text-primary">{b.client.phone}</a></div>}
                    {(b.client?.city || b.client?.province) && <div className="inline-flex items-center gap-1"><MapPin size={11} /> {[b.client.city, b.client.province].filter(Boolean).join(", ")}</div>}
                  </div>
                </div>

                <div className="md:col-span-2 md:border-l md:border-[#3D405B]/10 md:pl-4">
                  <div className="text-xs uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">Topic</div>
                  {b.topic ? (
                    <div className="text-xs text-[#3D405B] italic line-clamp-3" title={b.topic}>&ldquo;{b.topic}&rdquo;</div>
                  ) : (
                    <div className="text-xs text-[#7A7D96] italic">Not shared</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function AvailabilityReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports/availability");
      setData(data);
    } catch { toast.error("Could not load availability report"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const nudge = async (uid, name) => {
    if (!window.confirm(`Send an availability reminder email to ${name}?`)) return;
    setNudging(uid);
    try {
      await api.post(`/admin/counsellors/${uid}/nudge-availability`);
      toast.success(`Nudge sent to ${name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setNudging(null); }
  };

  const [bulkNudging, setBulkNudging] = useState(false);
  const nudgeAll = async () => {
    if (!window.confirm(`Send the "please add availability" email to every counsellor with 0 open slots?\n\nCounsellors already nudged in the past 7 days will be skipped automatically.`)) return;
    setBulkNudging(true);
    try {
      const { data: res } = await api.post("/admin/counsellors/nudge-availability-all");
      const bits = [];
      if (res.sent) bits.push(`${res.sent} sent`);
      if (res.skipped_recent) bits.push(`${res.skipped_recent} skipped (recent)`);
      if (res.skipped_have_slots) bits.push(`${res.skipped_have_slots} skipped (has slots)`);
      if (res.failed) bits.push(`${res.failed} failed`);
      toast.success(`Bulk nudge complete: ${bits.join(" · ") || "nothing to do"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk nudge failed"); }
    finally { setBulkNudging(false); }
  };

  if (loading) return <div className="text-sm text-[#7A7D96]">Loading…</div>;
  if (!data) return null;

  return (
    <div data-testid="report-availability">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <StatTile label="Total counsellors" value={data.total_counsellors} tone="primary" />
        <StatTile label="With open slots" value={data.covered_count} tone="secondary" sublabel={`in next ${data.horizon_days} days`} />
        <StatTile label="Empty calendars" value={data.empty_count} tone={data.empty_count > 0 ? "destructive" : "muted"} sublabel="need chasing" />
      </div>

      {data.empty_count > 0 && (
        <div className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-4">
          <div className="text-sm text-[#3D405B] leading-relaxed">
            <strong>{data.empty_count} counsellor{data.empty_count === 1 ? " has" : "s have"} no open slots</strong> in the next {data.horizon_days} days.
            Send them all the &ldquo;please add availability&rdquo; nudge in one click — anyone nudged in the past 7 days is skipped.
          </div>
          <button
            onClick={nudgeAll}
            disabled={bulkNudging}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white text-sm font-extrabold whitespace-nowrap shadow hover:-translate-y-0.5 transition disabled:opacity-60"
            data-testid="availability-nudge-all-btn"
          >
            <Bell size={14} /> {bulkNudging ? "Sending…" : `Nudge all ${data.empty_count}`}
          </button>
        </div>
      )}

      {data.rows.length === 0 ? (
        <div className="text-sm text-[#7A7D96]">No verified counsellors yet.</div>
      ) : (
        <div className="divide-y divide-[#3D405B]/5">
          {data.rows.map((r) => (
            <div key={r.user_id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`availability-row-${r.user_id}`}>
              <div className="flex-1 min-w-[200px]">
                <RouterLink to={`/admin/users/${r.user_id}`} className="font-bold text-[#3D405B] hover:text-primary">{r.name}</RouterLink>
                <div className="text-[11px] text-[#7A7D96]">{r.email}</div>
              </div>
              {r.is_empty ? (
                <span className="pill bg-destructive/10 text-destructive inline-flex items-center gap-1"><AlertTriangle size={11} /> 0 open slots</span>
              ) : (
                <span className="pill bg-secondary/15 text-secondary inline-flex items-center gap-1"><CalendarCheck size={11} /> {r.slots_next_14d} open · next {r.next_slot_at ? new Date(r.next_slot_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—"}</span>
              )}
              {r.last_availability_reminder_at && (
                <span className="text-[10px] text-[#7A7D96]" title={new Date(r.last_availability_reminder_at).toLocaleString()}>
                  Last nudge: {new Date(r.last_availability_reminder_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                </span>
              )}
              {r.is_empty && (
                <button
                  onClick={() => nudge(r.user_id, r.name)}
                  disabled={nudging === r.user_id}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-white text-xs font-bold hover:shadow disabled:opacity-50"
                  data-testid={`availability-nudge-${r.user_id}`}
                >
                  <Send size={11} /> {nudging === r.user_id ? "Sending…" : "Nudge"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// ---------- Attendance & outcomes report ----------
// Compact 4-column view of what happened to yesterday's bookings so the admin can
// see acceptance/no-show/feedback health at a glance. Backed by /api/admin/reports/attendance.
function AttendanceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysBack, setDaysBack] = useState(14);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/attendance?days_back=${daysBack}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load attendance"))
      .finally(() => setLoading(false));
  }, [daysBack]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  if (loading) return <div className="brand-card p-5 animate-pulse h-40" />;
  if (!data) return null;
  const t = data.totals || {};

  const StatTile = ({ label, count, tone, testid }) => (
    <div className={`brand-card p-4 ${tone === "danger" ? "bg-destructive/5 border-destructive/20" : tone === "warning" ? "bg-primary/5 border-primary/20" : "bg-muted/40"}`} data-testid={testid}>
      <div className={`text-3xl font-extrabold ${tone === "danger" ? "text-destructive" : tone === "warning" ? "text-primary" : "text-[#3D405B]"}`}>{count}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">{label}</div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="report-attendance">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#3D405B]">Window</label>
        <select
          value={daysBack}
          onChange={(e) => setDaysBack(Number(e.target.value))}
          className="text-sm rounded-xl border border-[#3D405B]/15 bg-white px-3 py-1.5 font-semibold text-[#3D405B]"
          data-testid="attendance-window"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Pending acceptance" count={t.pending_acceptance || 0} tone={t.pending_acceptance ? "warning" : "neutral"} testid="stat-pending" />
        <StatTile label="Auto-declined (36h)" count={t.auto_declined || 0} tone="danger" testid="stat-auto-declined" />
        <StatTile label="Declined by counsellor" count={t.declined || 0} testid="stat-declined" />
        <StatTile label="Counsellor feedback" count={t.outcomes || 0} testid="stat-outcomes" />
        <StatTile label="Client no-show" count={t.no_show_client || 0} tone="danger" testid="stat-no-show-client" />
        <StatTile label="Counsellor no-show" count={t.no_show_counsellor || 0} tone="danger" testid="stat-no-show-counsellor" />
        <StatTile label="No-show (both)" count={t.no_show_both || 0} tone="danger" testid="stat-no-show-both" />
      </div>

      {(data.pending_acceptance || []).length > 0 && (
        <div className="brand-card p-5" data-testid="attendance-pending-list">
          <div className="font-extrabold text-[#3D405B] mb-3">Awaiting counsellor Accept/Decline</div>
          <div className="space-y-2">
            {data.pending_acceptance.map((b) => (
              <div key={b.booking_id} className="flex flex-wrap items-center gap-3 text-sm text-[#3D405B]">
                <span className="pill bg-primary/10 text-primary">{fmt(b.scheduled_at)}</span>
                <span className="font-bold">{b.counsellor?.name}</span>
                <span className="text-[#7A7D96]">←</span>
                <span>{b.client?.name}</span>
                {b.acceptance_deadline && <span className="text-xs text-[#7A7D96]">deadline {fmt(b.acceptance_deadline)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.no_shows || []).length > 0 && (
        <div className="brand-card p-5" data-testid="attendance-noshow-list">
          <div className="font-extrabold text-[#3D405B] mb-3">Recent no-shows</div>
          <div className="space-y-2">
            {data.no_shows.slice(0, 25).map((b) => (
              <div key={b.booking_id} className="flex flex-wrap items-center gap-3 text-sm text-[#3D405B]">
                <span className="pill bg-destructive/10 text-destructive">{b.status.replace(/_/g, " ")}</span>
                <span className="font-bold">{fmt(b.scheduled_at)}</span>
                <span>{b.counsellor?.name}</span>
                <span className="text-[#7A7D96]">↔</span>
                <span>{b.client?.name}</span>
                {b.outcome_reason && <span className="pill bg-muted text-[#5F627D]">said: {b.outcome_reason.replace(/_/g, " ")}</span>}
                {b.outcome_notes && <span className="text-xs text-[#7A7D96] italic">&ldquo;{b.outcome_notes}&rdquo;</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.outcomes || []).length > 0 && (
        <div className="brand-card p-5" data-testid="attendance-outcomes-list">
          <div className="font-extrabold text-[#3D405B] mb-3">Recent counsellor feedback</div>
          <div className="space-y-2">
            {data.outcomes.slice(0, 25).map((b) => (
              <div key={b.booking_id} className="text-sm text-[#3D405B] flex flex-wrap items-center gap-2">
                <span className={`pill ${b.outcome_happened ? "bg-secondary/15 text-secondary" : "bg-destructive/10 text-destructive"}`}>{b.outcome_happened ? "Happened" : "Didn't happen"}</span>
                <span className="font-bold">{fmt(b.scheduled_at)}</span>
                <span>{b.counsellor?.name}</span>
                <span className="text-[#7A7D96]">↔</span>
                <span>{b.client?.name}</span>
                {b.outcome_reason && <span className="text-xs text-[#7A7D96]">Reason: {b.outcome_reason.replace(/_/g, " ")}</span>}
                {b.outcome_notes && <span className="text-xs text-[#7A7D96] italic">&ldquo;{b.outcome_notes}&rdquo;</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// ---------- Shared date-range picker used by the roster/new/past reports ----------
function DateRangeControls({ start, end, setStart, setEnd, testidPrefix }) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">Start</span>
        <input
          type="date"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          className="text-sm rounded-xl border border-[#3D405B]/15 bg-white px-3 py-1.5 font-semibold text-[#3D405B]"
          data-testid={`${testidPrefix}-start`}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">End</span>
        <input
          type="date"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          className="text-sm rounded-xl border border-[#3D405B]/15 bg-white px-3 py-1.5 font-semibold text-[#3D405B]"
          data-testid={`${testidPrefix}-end`}
        />
      </label>
    </div>
  );
}

const _todayISO = () => new Date().toISOString().slice(0, 10);
const _daysAgoISO = (n) => new Date(Date.now() - n * 86400_000).toISOString().slice(0, 10);
const _daysFromNowISO = (n) => new Date(Date.now() + n * 86400_000).toISOString().slice(0, 10);
const _fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

function NewBookingsReport() {
  const [start, setStart] = useState(_daysAgoISO(7));
  const [end, setEnd] = useState(_todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/new-bookings?start=${start}&end=${end}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load new bookings"))
      .finally(() => setLoading(false));
  }, [start, end]);
  return (
    <div className="space-y-3" data-testid="report-new-bookings">
      <DateRangeControls start={start} end={end} setStart={setStart} setEnd={setEnd} testidPrefix="new-bookings" />
      {loading ? <div className="brand-card p-5 animate-pulse h-32" /> : (
        <>
          <div className="text-xs font-bold text-[#7A7D96] uppercase tracking-[0.14em]">{data?.total || 0} bookings created</div>
          <div className="brand-card p-4 divide-y divide-[#3D405B]/10">
            {(data?.rows || []).length === 0 ? (
              <div className="text-sm text-[#7A7D96] py-2">No bookings in this window.</div>
            ) : (data?.rows || []).map((b) => (
              <div key={b.booking_id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`new-booking-row-${b.booking_id}`}>
                <span className="pill bg-primary/10 text-primary">Created {_fmtDateTime(b.created_at)}</span>
                <span className="font-bold text-[#3D405B]">{b.client?.name}</span>
                <span className="text-[#7A7D96]">→</span>
                <span className="font-bold text-[#3D405B]">{b.counsellor?.name}</span>
                <span className="text-[#5F627D]">for {_fmtDateTime(b.scheduled_at)}</span>
                <span className={`pill ${b.status === "scheduled" ? "bg-secondary/15 text-secondary" : b.status === "pending_acceptance" ? "bg-primary/10 text-primary" : "bg-muted text-[#5F627D]"}`}>{b.status?.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RosterReport() {
  const [start, setStart] = useState(_todayISO());
  const [end, setEnd] = useState(_daysFromNowISO(7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/roster?start=${start}&end=${end}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load roster"))
      .finally(() => setLoading(false));
  }, [start, end]);

  // Build a plain-text roster summary that can travel over email OR WhatsApp intact —
  // WhatsApp's web/deeplink share only accepts a `text` param, so a human-readable
  // multi-line format works best on both channels.
  const rosterAsText = () => {
    const header = `Hellofoundation roster · ${start} to ${end} (${data?.total || 0} sessions)\n`;
    const lines = (data?.rows || []).map((b) => {
      const when = _fmtDateTime(b.scheduled_at);
      const mode = b.session_mode === "in_person" ? "In person" : "Online";
      const contact = [b.client?.phone, b.client?.email].filter(Boolean).join(" · ");
      return `• ${when} — ${b.counsellor?.name || "?"} ↔ ${b.client?.name || "?"} (${mode})${contact ? `\n  Client: ${contact}` : ""}`;
    }).join("\n");
    return `${header}\n${lines || "(no sessions)"}`;
  };

  const shareViaEmail = () => {
    const subject = `Hellofoundation roster ${start} → ${end}`;
    const body = rosterAsText();
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareViaWhatsApp = () => {
    const text = rosterAsText();
    // wa.me works from desktop AND mobile — opens WhatsApp Web / native app respectively.
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <div className="space-y-3" data-testid="report-roster">
      <DateRangeControls start={start} end={end} setStart={setStart} setEnd={setEnd} testidPrefix="roster" />
      {loading ? <div className="brand-card p-5 animate-pulse h-32" /> : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-bold text-[#7A7D96] uppercase tracking-[0.14em]">{data?.total || 0} sessions on the roster</div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={shareViaEmail}
                disabled={!data?.rows?.length}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-white text-xs font-extrabold shadow-sm hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 transition-all"
                data-testid="roster-share-email"
              >
                <Mail size={13} /> Share via email
              </button>
              <button
                onClick={shareViaWhatsApp}
                disabled={!data?.rows?.length}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-white text-xs font-extrabold shadow-sm hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 transition-all"
                data-testid="roster-share-whatsapp"
              >
                <MessageCircle size={13} /> Share via WhatsApp
              </button>
            </div>
          </div>
          <div className="brand-card p-4 divide-y divide-[#3D405B]/10">
            {(data?.rows || []).length === 0 ? (
              <div className="text-sm text-[#7A7D96] py-2">Nothing scheduled in this window.</div>
            ) : (data?.rows || []).map((b) => (
              <div key={b.booking_id} className="py-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-start text-sm" data-testid={`roster-row-${b.booking_id}`}>
                <div className="md:col-span-2 font-extrabold text-[#3D405B]">{_fmtDateTime(b.scheduled_at)}</div>
                <div className="md:col-span-4">
                  <div className="text-[11px] text-[#7A7D96] uppercase tracking-[0.14em] font-bold">Counsellor</div>
                  <div className="font-bold text-[#3D405B]">{b.counsellor?.name}</div>
                  <div className="text-[11px] text-[#5F627D] space-y-0.5 mt-0.5">
                    {b.counsellor?.email && (
                      <a href={`mailto:${b.counsellor.email}`} className="inline-flex items-center gap-1 hover:text-primary truncate max-w-full" data-testid={`roster-counsellor-email-${b.booking_id}`}>
                        <Mail size={11} /> <span className="truncate">{b.counsellor.email}</span>
                      </a>
                    )}
                    {b.counsellor?.phone && (
                      <div>
                        <a href={`tel:${b.counsellor.phone}`} className="inline-flex items-center gap-1 hover:text-primary mr-2" data-testid={`roster-counsellor-phone-${b.booking_id}`}>
                          <Phone size={11} /> {b.counsellor.phone}
                        </a>
                        <a href={`https://wa.me/${b.counsellor.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-secondary hover:underline text-[11px]" data-testid={`roster-counsellor-wa-${b.booking_id}`}>
                          <MessageCircle size={11} /> WA
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:col-span-4">
                  <div className="text-[11px] text-[#7A7D96] uppercase tracking-[0.14em] font-bold">Client</div>
                  <div className="font-bold text-[#3D405B]">{b.client?.name}</div>
                  <div className="text-[11px] text-[#5F627D] space-y-0.5 mt-0.5">
                    {b.client?.email && (
                      <a href={`mailto:${b.client.email}`} className="inline-flex items-center gap-1 hover:text-primary truncate max-w-full" data-testid={`roster-client-email-${b.booking_id}`}>
                        <Mail size={11} /> <span className="truncate">{b.client.email}</span>
                      </a>
                    )}
                    {b.client?.phone && (
                      <div>
                        <a href={`tel:${b.client.phone}`} className="inline-flex items-center gap-1 hover:text-primary mr-2" data-testid={`roster-client-phone-${b.booking_id}`}>
                          <Phone size={11} /> {b.client.phone}
                        </a>
                        <a href={`https://wa.me/${b.client.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-secondary hover:underline text-[11px]" data-testid={`roster-client-wa-${b.booking_id}`}>
                          <MessageCircle size={11} /> WA
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2 flex flex-wrap gap-1">
                  <span className={`pill ${b.status === "scheduled" ? "bg-secondary/15 text-secondary" : "bg-primary/10 text-primary"}`}>{b.status?.replace(/_/g, " ")}</span>
                  <span className={`pill ${b.session_mode === "in_person" ? "bg-accent/30 text-[#3D405B]" : "bg-muted text-[#5F627D]"}`}>{b.session_mode === "in_person" ? "In person" : "Online"}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PastBookingsReport() {
  const [start, setStart] = useState(_daysAgoISO(7));
  const [end, setEnd] = useState(_todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/past-bookings?start=${start}&end=${end}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load past bookings"))
      .finally(() => setLoading(false));
  }, [start, end]);
  const fmtWait = (s) => {
    if (s === null || s === undefined) return "—";
    if (s < 60) return `${s}s`;
    return `${Math.round(s / 60)}m`;
  };
  return (
    <div className="space-y-3" data-testid="report-past-bookings">
      <DateRangeControls start={start} end={end} setStart={setStart} setEnd={setEnd} testidPrefix="past-bookings" />
      {loading ? <div className="brand-card p-5 animate-pulse h-40" /> : (
        <>
          {data?.kpis && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="brand-card p-3" data-testid="past-kpi-total"><div className="text-2xl font-extrabold text-[#3D405B]">{data.kpis.sessions_total}</div><div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">Total in range</div></div>
              <div className="brand-card p-3" data-testid="past-kpi-completed"><div className="text-2xl font-extrabold text-secondary">{data.kpis.sessions_completed}</div><div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">Completed</div></div>
              <div className="brand-card p-3" data-testid="past-kpi-no-show"><div className="text-2xl font-extrabold text-destructive">{data.kpis.sessions_no_show}</div><div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">No-shows</div></div>
              <div className="brand-card p-3" data-testid="past-kpi-cancelled"><div className="text-2xl font-extrabold text-[#3D405B]">{data.kpis.sessions_cancelled}</div><div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">Cancelled</div></div>
              <div className="brand-card p-3" data-testid="past-kpi-avg-wait"><div className="text-2xl font-extrabold text-primary">{fmtWait(data.kpis.avg_wait_seconds)}</div><div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">Avg client wait</div></div>
            </div>
          )}
          <div className="brand-card p-4 divide-y divide-[#3D405B]/10">
            {(data?.rows || []).length === 0 ? (
              <div className="text-sm text-[#7A7D96] py-2">No sessions in this window.</div>
            ) : (data?.rows || []).map((b) => (
              <div key={b.booking_id} className="py-3 space-y-1 text-sm" data-testid={`past-row-${b.booking_id}`}>
                <div className="flex flex-wrap items-center gap-2 text-[#3D405B]">
                  <span className="font-extrabold">{_fmtDateTime(b.scheduled_at)}</span>
                  <span className="pill bg-muted text-[#5F627D]">{b.status?.replace(/_/g, " ")}</span>
                  <span className="font-bold">{b.counsellor?.name}</span>
                  <span className="text-[#7A7D96]">↔</span>
                  <span className="font-bold">{b.client?.name}</span>
                </div>
                <div className="text-xs text-[#5F627D] flex flex-wrap gap-x-4 gap-y-1">
                  <span><strong>Scheduled:</strong> {b.duration_minutes || 50}m</span>
                  {b.actual_minutes !== null && <span><strong>Actual:</strong> {b.actual_minutes}m</span>}
                  {b.started_at && <span><strong>Started:</strong> {_fmtDateTime(b.started_at)}</span>}
                  {b.client_joined_at && <span><strong>Client joined:</strong> {_fmtDateTime(b.client_joined_at)}</span>}
                  <span><strong>Wait:</strong> {fmtWait(b.wait_time_seconds)}</span>
                </div>
                {b.summary_client && (
                  <div className="text-xs text-[#5F627D] italic bg-muted/40 rounded-lg px-2 py-1 mt-1">
                    <strong className="not-italic text-[#3D405B]">Summary:</strong> &ldquo;{b.summary_client}&rdquo;
                  </div>
                )}
                {b.outcome_reason && (
                  <div className="text-xs text-[#5F627D] flex gap-2">
                    <span className={`pill ${b.outcome_happened ? "bg-secondary/15 text-secondary" : "bg-destructive/10 text-destructive"}`}>Feedback: {b.outcome_happened ? "happened" : "didn't happen"}</span>
                    <span className="pill bg-muted text-[#5F627D]">{b.outcome_reason.replace(/_/g, " ")}</span>
                    {b.outcome_notes && <span className="italic">&ldquo;{b.outcome_notes}&rdquo;</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function HourlyRateSettings({ year }) {
  const [rates, setRates] = useState([]);
  const [effective, setEffective] = useState(null);
  const [defaultRate, setDefaultRate] = useState(850);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/settings/hourly-rates")
      .then((r) => {
        setRates(r.data?.rates || []);
        setEffective(r.data?.effective_current_year);
        setDefaultRate(r.data?.default_rate_zar);
        // Pre-populate the input with the currently-selected year's rate if it exists.
        const existing = (r.data?.rates || []).find((x) => x.year === year);
        setDraft(existing ? String(existing.rate_zar) : "");
      })
      .catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const save = async () => {
    const amount = parseFloat(draft);
    if (isNaN(amount) || amount < 0 || amount > 100000) {
      toast.error("Enter a rate between 0 and 100000");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings/hourly-rates", { year, rate_zar: amount });
      toast.success(`Rate for ${year} saved — future certificates will use R${amount}/hr`);
      load();
    } catch (e) {
      toast.error("Could not save rate");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (yr) => {
    if (!window.confirm(`Remove the R rate for ${yr}? Certificates for ${yr} will fall back to the most recent year's rate.`)) return;
    try {
      await api.delete(`/admin/settings/hourly-rates/${yr}`);
      toast.success(`Removed rate for ${yr}`);
      load();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="brand-card p-4 bg-primary/5 border-primary/20" data-testid="hourly-rate-settings">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">Tax certificate rate</div>
          <div className="font-extrabold text-[#3D405B] text-sm mt-0.5">Set the hourly counselling rate used for {year} S18A certificates.</div>
          <div className="text-[11px] text-[#7A7D96] mt-1">Effective for the current year: <strong className="text-[#3D405B]">R {(effective ?? defaultRate)?.toLocaleString()}/hr</strong> · Default fallback: R {defaultRate?.toLocaleString()}/hr</div>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">Rate for {year} (ZAR/hr)</span>
            <input
              type="number"
              step="10"
              min="0"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`e.g. ${defaultRate}`}
              className="text-sm rounded-xl border border-[#3D405B]/15 bg-white px-3 py-1.5 font-semibold text-[#3D405B] w-32"
              data-testid="hourly-rate-input"
            />
          </label>
          <button
            onClick={save}
            disabled={saving || !draft}
            className="px-4 py-1.5 rounded-xl bg-primary text-white text-xs font-extrabold shadow-sm hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 transition-all"
            data-testid="hourly-rate-save"
          >
            {saving ? "…" : "Save"}
          </button>
        </div>
      </div>
      {rates.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {rates.map((r) => (
            <div key={r.year} className="inline-flex items-center gap-2 pill bg-white border border-[#3D405B]/15" data-testid={`hourly-rate-row-${r.year}`}>
              <span className="text-[#3D405B]"><strong>{r.year}</strong> · R {r.rate_zar.toLocaleString()}/hr</span>
              <button
                onClick={() => remove(r.year)}
                className="text-destructive/70 hover:text-destructive"
                title={`Remove ${r.year} rate`}
                data-testid={`hourly-rate-delete-${r.year}`}
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContributionReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(""); // "" = whole year
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ year: String(year) });
    if (month) q.set("month", String(month));
    api.get(`/admin/reports/contribution?${q.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load contribution report"))
      .finally(() => setLoading(false));
  }, [year, month]);
  const years = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);
  return (
    <div className="space-y-3" data-testid="report-contribution">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">Year</span>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="text-sm rounded-xl border border-[#3D405B]/15 bg-white px-3 py-1.5 font-semibold text-[#3D405B]" data-testid="contribution-year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase font-extrabold tracking-[0.14em] text-[#7A7D96]">Month</span>
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="text-sm rounded-xl border border-[#3D405B]/15 bg-white px-3 py-1.5 font-semibold text-[#3D405B]" data-testid="contribution-month">
            <option value="">Whole year</option>
            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
        </label>
      </div>
      {loading ? <div className="brand-card p-5 animate-pulse h-32" /> : (
        <>
          {data?.totals && (
            <div className="grid grid-cols-3 gap-3">
              <div className="brand-card p-4" data-testid="contribution-total-hours">
                <div className="text-3xl font-extrabold text-secondary">{data.totals.hours_donated}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">Total hours donated</div>
              </div>
              <div className="brand-card p-4" data-testid="contribution-total-sessions">
                <div className="text-3xl font-extrabold text-[#3D405B]">{data.totals.sessions_delivered}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">Sessions delivered</div>
              </div>
              <div className="brand-card p-4" data-testid="contribution-total-people">
                <div className="text-3xl font-extrabold text-primary">{data.totals.people_helped}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">Unique people helped</div>
              </div>
            </div>
          )}
          <div className="brand-card p-4 divide-y divide-[#3D405B]/10">
            {(data?.counsellors || []).length === 0 ? (
              <div className="text-sm text-[#7A7D96] py-2">No counsellors yet.</div>
            ) : (data?.counsellors || []).map((c) => (
              <div key={c.user_id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`contribution-row-${c.user_id}`}>
                <div className="flex-1 min-w-[160px]">
                  <div className="font-extrabold text-[#3D405B]">{c.name}</div>
                  {c.email && <div className="text-xs text-[#7A7D96]"><a href={`mailto:${c.email}`} className="hover:text-primary">{c.email}</a></div>}
                </div>
                <div className="text-right">
                  <div className="text-lg font-extrabold text-secondary">{c.hours_donated}h</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96]">Donated</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-extrabold text-[#3D405B]">{c.sessions_delivered}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96]">Sessions</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-extrabold text-primary">{c.people_helped}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96]">People</div>
                </div>
                <div className="flex gap-1.5">
                  <a
                    href={`${api.defaults.baseURL || ""}/admin/counsellors/${c.user_id}/certificate?year=${year}&doc_type=appreciation`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary text-white text-[10px] font-extrabold hover:-translate-y-0.5 transition-all"
                    data-testid={`contribution-letter-${c.user_id}`}
                    title="Download appreciation letter"
                  >
                    <Mail size={11} /> Letter
                  </a>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

